import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Select, Input, Button, Space, Tag, App, Typography, Spin, Row, Col, Empty, Modal, Alert,
  Slider, Radio, Switch, Divider, Image, Tooltip, Segmented, Upload, Badge, Progress, Collapse, Popover, Tabs, Popconfirm, Table, Form,
} from 'antd';
import {
  VideoCameraOutlined,
  PictureOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  DeleteOutlined,
  EditOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
  UploadOutlined,
  PlusOutlined,
  StarFilled,
  FireOutlined,
  ExperimentOutlined,
  BulbOutlined,
  ExpandOutlined,
  ReloadOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  LeftOutlined,
  RightOutlined,
  RobotOutlined,
  ScanOutlined,
  AudioOutlined,
  SoundOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { t } from '../utils/i18n';
import api, { getFullApiUrl } from '../services/api';
import { multimodalAPI, modelAPI, userAPI } from '../services/api';
import JSZip from 'jszip';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ========== 模型版本特性定义 ==========
const MODEL_FEATURES = {
  video: {
    'seedance-2.0': {
      version: '2.0', tier: 'Standard', tierColor: '#ff4d4f',
      desc: 'Seedance 2.0 flagship video generation',
      features: ['1080p Ultra HD', '15s Duration', 'Multimodal Reference', 'Audio Generation', 'First/Last Frame', 'Smart Multi-Frame'],
      icon: <FireOutlined />,
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
        { value: '1080p', label: '1080p 超清', disabled: false },
      ],
      durationRange: [4, 15],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', 'adaptive'],
      maxRefImages: 10,
      maxRefVideos: 3,
      maxRefAudio: 3,
    },
    'seedance-2.0-fast': {
      version: '2.0 Fast', tier: 'Fast', tierColor: '#52c41a',
      desc: 'Seedance 2.0 Fast, 720p faster output',
      features: ['720p HD', '15s Duration', 'Fast Output', 'Multimodal Reference'],
      icon: <ThunderboltOutlined />,
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
      ],
      durationRange: [4, 15],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 10,
      maxRefVideos: 3,
      maxRefAudio: 3,
    },
    'seedance-1.5-pro': {
      version: '1.5 Pro', tier: 'Pro', tierColor: '#fa8c16',
      desc: 'Seedance 1.5 Pro, first/last frame + audio',
      features: ['720p HD', '12s Duration', 'Audio Generation', 'First Frame', 'Last Frame'],
      icon: <VideoCameraOutlined />,
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
      ],
      durationRange: [4, 12],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 0,
      maxRefVideos: 0,
      maxRefAudio: 0,
    },
    'seedance-2.0-idle': {
      version: '2.0 Idle', tier: 'Super-Res', tierColor: '#722ed1',
      desc: 'Seedance 2.0 Idle super-resolution, ref video with discount',
      features: ['1080p Ultra HD', '15s Duration', 'Super Resolution', 'Multimodal Reference'],
      resolutions: [
        { value: '720p', label: '720p 高清', disabled: false },
        { value: '1080p', label: '1080p 超清', disabled: false },
      ],
      durationRange: [4, 15],
      aspectRatios: ['16:9'],
      maxRefImages: 10, maxRefVideos: 3, maxRefAudio: 3,
    },
    'seedance-2.0-fast-idle': {
      version: '2.0 Fast Idle', tier: 'Super-Res Fast', tierColor: '#531dab',
      desc: 'Seedance 2.0 Fast Idle super-resolution, ref video with discount',
      features: ['1080p Ultra HD', '15s Duration', 'Super Resolution', 'Fast Output'],
      resolutions: [
        { value: '720p', label: '720p 高清', disabled: false },
        { value: '1080p', label: '1080p 超清', disabled: false },
      ],
      durationRange: [4, 15],
      aspectRatios: ['16:9'],
      maxRefImages: 10, maxRefVideos: 3, maxRefAudio: 3,
    },
  },
  image: {
    'seedream-4.0': {
      version: '4.0', tier: 'Standard', tierColor: '#ff4d4f',
      desc: 'Seedream 4.0 flagship image generation',
      features: ['2048px Ultra HD', 'Multi-style', 'Extreme Detail', 'Image-to-Image'],
      icon: <FireOutlined />,
      sizes: [
        { value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' },
        { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' },
        { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' },
        { value: '2048x2048', label: '2048×2048 (1:1)', aspect: '1:1' },
      ],
      maxImages: 1,
      maxRefImages: 10,
      quality_pricing: { low: 0.030, medium: 0.030, high: 0.030, auto: 0.030 },
      styles: [
        { value: 'photorealistic', label: 'Photorealistic', emoji: '📷' },
        { value: 'illustration', label: 'Illustration', emoji: '🎨' },
        { value: 'anime', label: 'Anime', emoji: '🎌' },
        { value: 'oil-painting', label: '油画', emoji: '🖼️' },
        { value: '3d-render', label: '3D Render', emoji: '💎' },
        { value: 'watercolor', label: '水彩', emoji: '🖌️' },
        { value: 'sketch', label: 'Sketch', emoji: '✏️' },
        { value: 'pixel-art', label: '像素', emoji: '👾' },
      ],
      guideScaleRange: [1, 20],
      guideScaleDefault: 7.5,
    },
    'seedream-4.5': {
      version: '4.5', tier: 'Pro', tierColor: '#fa8c16',
      desc: 'Seedream 4.5 图片生成，良好细节',
      features: ['2048px超高清', '多风格', '良好细节', '图生图'],
      icon: <StarFilled />,
      sizes: [
        { value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' },
        { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' },
        { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' },
        { value: '2048x2048', label: '2048×2048 (1:1)', aspect: '1:1' },
      ],
      maxImages: 1,
      maxRefImages: 10,
      quality_pricing: { low: 0.040, medium: 0.040, high: 0.040, auto: 0.040 },
      styles: [
        { value: 'photorealistic', label: 'Photorealistic', emoji: '📷' },
        { value: 'illustration', label: 'Illustration', emoji: '🎨' },
        { value: 'anime', label: 'Anime', emoji: '🎌' },
        { value: 'oil-painting', label: '油画', emoji: '🖼️' },
        { value: '3d-render', label: '3D Render', emoji: '💎' },
        { value: 'watercolor', label: '水彩', emoji: '🖌️' },
      ],
      guideScaleRange: [1, 20],
      guideScaleDefault: 7.5,
    },
    'seedream-5.0-lite': {
      version: '5.0 Lite', tier: 'Lite', tierColor: '#52c41a',
      desc: 'Seedream 5.0 Lite 快速图片生成，高性价比',
      features: ['2048px超高清', '快速出图', '高性价比'],
      icon: <ThunderboltOutlined />,
      sizes: [
        { value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' },
        { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' },
        { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' },
      ],
      maxImages: 1,
      maxRefImages: 10,
      quality_pricing: { low: 0.035, medium: 0.035, high: 0.035, auto: 0.035 },
      styles: [
        { value: 'photorealistic', label: 'Photorealistic', emoji: '📷' },
        { value: 'illustration', label: 'Illustration', emoji: '🎨' },
        { value: 'anime', label: 'Anime', emoji: '🎌' },
        { value: 'oil-painting', label: '油画', emoji: '🖼️' },
      ],
      guideScaleRange: [1, 20],
      guideScaleDefault: 7.5,
    },
  },
};

// 格式化时间显示
const formatTimeDisplay = (isoStr) => {
  try {
    const d = new Date(isoStr);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return isoStr || '-'; }
};

// 格式化时间显示（短版，用于列表）
const formatTimeShort = (isoStr) => {
  try {
    const d = new Date(isoStr);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return isoStr || '-'; }
};

// 复制文本
const copyText = async (text, message) => {
  try {
    await navigator.clipboard.writeText(text);
    message?.success(t('copied'));
  } catch {
    // fallback
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    message?.success(t('copied'));
  }
};

// 状态配置
const getStatusConfig = () => ({
  queued: { color: 'processing', icon: <ClockCircleOutlined />, text: t('queueing') },
  pending: { color: 'processing', icon: <ClockCircleOutlined />, text: t('queueing') },
  processing: { color: 'processing', icon: <SyncOutlined spin />, text: t('generating_progress') },
  running: { color: 'processing', icon: <SyncOutlined spin />, text: t('generating_progress') },
  active: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },       // ImageRecord 使用 active 表示成功
  completed: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  succeeded: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  success: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  failed: { color: 'error', icon: <CloseCircleOutlined />, text: t('failed') },
  error: { color: 'error', icon: <CloseCircleOutlined />, text: t('failed') },
  cancelled: { color: 'default', icon: <CloseCircleOutlined />, text: t('canceled') },
  expired: { color: 'default', icon: <CloseCircleOutlined />, text: t('canceled') },
  canceled: { color: 'default', icon: <CloseCircleOutlined />, text: t('canceled') },
});

const CreativeInspiration = () => {
  // 从 App 组件获取 message 实例（antd v5 要求）
  const { message } = App.useApp();

  // 核心状态
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('creativeTab') || 'video');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);

  // 视频参数
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('720p');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [videoSeed, setVideoSeed] = useState(null);
  const [useRandomSeed, setUseRandomSeed] = useState(true);
  const [watermark, setWatermark] = useState(false);
  const [videoNegativePrompt, setVideoNegativePrompt] = useState('');
  const [cameraFixed, setCameraFixed] = useState(false);
  const [guidanceScale, setGuidanceScale] = useState(7.5); // 仅图片tab使用

  // 图片参数
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageCount, setImageCount] = useState(1);
  const [imageStyle, setImageStyle] = useState('photorealistic');
  const [imageQuality, setImageQuality] = useState('medium');
  const [imageSeed, setImageSeed] = useState(null);
  const [useRandomImageSeed, setUseRandomImageSeed] = useState(true);

  // 视频{t('video_mode_label')}
  const [videoMode, setVideoMode] = useState('full_reference'); // full_reference | start_end_frames | smart_multiframe
  // 首尾帧模式
  const [firstFrameImage, setFirstFrameImage] = useState(null);
  const [lastFrameImage, setLastFrameImage] = useState(null);
  // 智能多帧模式
  const [openingText, setOpeningText] = useState("");
  const [openingDuration, setOpeningDuration] = useState(2);
  const [multiframes, setMultiframes] = useState([]); // [{id, imageUrl, transitionPrompt, transitionDuration}]
  // 全能参考：扩展素材
  const [referenceVideos, setReferenceVideos] = useState([]);
  const [referenceAudio, setReferenceAudio] = useState([]);
  // 有声视频
  const [generateAudio, setGenerateAudio] = useState(false);
  const [returnLastFrame, setReturnLastFrame] = useState(false);
  // AI Prompt 弹窗
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalIntent, setAiModalIntent] = useState('');
  const [aiGeneratedGroups, setAiGeneratedGroups] = useState([]); // [{prompt, negative_prompt, title}]
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGroupCount, setAiGroupCount] = useState(2); // 生成几组提示词
  const [aiModel, setAiModel] = useState('deepseek-chat'); // AI 文本模型选择
  const [textModels, setTextModels] = useState([]); // 系统可用的文本模型列表

  // 参考图
  const [refImages, setRefImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // 临时素材库
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);

  // 人物素材库（XRToken Asset）- 3个Tab
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetTab, setAssetTab] = useState('works'); // works | aigc | realperson
  const [assetGroups, setAssetGroups] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [selectedAssetGroupId, setSelectedAssetGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newAssetUrl, setNewAssetUrl] = useState('');
  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetType, setNewAssetType] = useState('Image');
  const [uploadFile, setUploadFile] = useState(null);
  const [faceConsent, setFaceConsent] = useState(null);
  const [livenessState, setLivenessState] = useState('idle');
  const [livenessData, setLivenessData] = useState(null);
  const [livenessModalOpen, setLivenessModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [userId, setUserId] = useState('');
  const [editingGroupId, setEditingGroupId] = useState('');
  const [editingGroupName, setEditingGroupName] = useState('');
  const livenessTimerRef = { current: null };
  const livenessTimeoutRef = { current: null };
  const [imageLibraryItems, setImageLibraryItems] = useState([]);
  const [imageLibraryLoading, setImageLibraryLoading] = useState(false);
  const [imageLibraryPage, setImageLibraryPage] = useState(1);
  const [imageLibraryTotal, setImageLibraryTotal] = useState(0);
  const [imageLibraryTarget, setImageLibraryTarget] = useState(''); // 'refImages' | 'firstFrame' | 'lastFrame' | 'multiframe_N' | 'referenceVideos'
  const [materialTab, setMaterialTab] = useState('image'); // 'image' | 'video'
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [libraryMainTab, setLibraryMainTab] = useState('works'); // works | aigc | realperson
  const [imgErrors, setImgErrors] = useState(new Set()); // 追踪图片加载失败的项 ID

  // 历史列表缩略图幻灯片轮播
  const [thumbSlides, setThumbSlides] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  useEffect(() => {
    const multiItems = history.filter(h => h.type === 'image' && (h.imageUrls?.length || 0) > 1 && (h.status === 'completed' || h.status === 'active' || h.status === 'succeeded'));
    if (multiItems.length === 0) return;
    const timer = setInterval(() => {
      setThumbSlides(prev => {
        const next = { ...prev };
        multiItems.forEach(item => {
          // 鼠标悬停时不自动轮播
          if (hoveredId === item.id) return;
          const count = item.imageUrls.length;
          const cur = prev[item.id] || 0;
          next[item.id] = (cur + 1) % count;
        });
        return next;
      });
    }, 2500);
    return () => clearInterval(timer);
  }, [history, hoveredId]);

  // 手动切换缩略图幻灯片
  const handleThumbSlide = (e, itemId, direction, count) => {
    e.stopPropagation();
    setThumbSlides(prev => {
      const cur = prev[itemId] || 0;
      const next = direction === 'prev'
        ? (cur - 1 + count) % count
        : (cur + 1) % count;
      return { ...prev, [itemId]: next };
    });
  };

  // 轮询
  const pollTimersRef = useRef({});

  // 模型过滤 - 只保留 seedance（视频）和 seedream（图片）
  const videoModels = models.filter(m => 
    (m.name?.toLowerCase().includes('seedance') || m.model_name?.toLowerCase().includes('seedance'))
  );
  const imageModels = models.filter(m => 
    (m.name?.toLowerCase().includes('seedream') || m.model_name?.toLowerCase().includes('seedream'))
  );
  const currentModels = activeTab === 'video' ? videoModels : imageModels;

  // 当前模型特性 - 优先从 API 返回的 metadata JSON 读取，回退到硬编码 MODEL_FEATURES
  const currentModelKey = selectedModel;
  const currentFeat = (() => {
    const hc = MODEL_FEATURES[activeTab]?.[currentModelKey];
    const apiModel = currentModels.find(m => (m.name || m.model_name) === currentModelKey);
    if (apiModel?.metadata) {
      try {
        const meta = typeof apiModel.metadata === 'string' ? JSON.parse(apiModel.metadata) : apiModel.metadata;
        return { ...hc, ...meta, features: meta.features || hc?.features || [], resolutions: meta.resolutions || hc?.resolutions, durationRange: meta.durationRange || hc?.durationRange, fpsOptions: meta.fpsOptions || hc?.fpsOptions, aspectRatios: meta.aspectRatios || hc?.aspectRatios, maxRefImages: meta.maxRefImages ?? hc?.maxRefImages, maxRefVideos: meta.maxRefVideos ?? hc?.maxRefVideos, maxRefAudio: meta.maxRefAudio ?? hc?.maxRefAudio, maxImages: meta.maxImages ?? hc?.maxImages, guideScaleRange: meta.guideScaleRange ?? hc?.guideScaleRange, guideScaleDefault: meta.guideScaleDefault ?? hc?.guideScaleDefault, sizes: meta.sizes || meta.resolutions || hc?.sizes, styles: meta.styles || hc?.styles, videoModes: meta.videoModes || hc?.videoModes };
      } catch(e) {}
    }
    return hc || { features: [], resolutions: [], durationRange: [4,10], fpsOptions: [24], aspectRatios: [], sizes: [], styles: [], videoModes: [] };
  })();

  useEffect(() => {
    fetchModels();
  }, []);

  // 从数据库拉取历史记录
  const refreshServerHistory = useCallback(async () => {
    try {
      const [videoRes, imageRes] = await Promise.allSettled([
        multimodalAPI.getVideoHistory(1, 50),
        multimodalAPI.getImageHistory(1, 50),
      ]);
      const items = [];

      // 解析 reference 字段（后端返回 JSON 字符串数组）
      const parseRefArray = (val) => {
        if (!val || val === 'null') return [];
        try {
          const parsed = typeof val === 'string' ? JSON.parse(val) : val;
          return Array.isArray(parsed) ? parsed : [];
        } catch { return []; }
      };

      // 映射视频记录
      if (videoRes.status === 'fulfilled' && videoRes.value?.items) {
        videoRes.value.items.forEach(item => {
          // 优先使用 API 返回的 status，expired 优先级最高
          let status = item.status || 'succeeded';
          if (item.expired) status = 'expired';
          items.push({
            id: `i-${item.id}`,
            type: 'video',
            model: item.model,
            prompt: item.prompt,
            taskId: item.task_id || null,
            status,
            errorMsg: item.error_msg || '',
            videoUrl: item.video_url || '',
            duration: item.duration || 0,
            resolution: item.resolution,
            refImages: parseRefArray(item.reference_images),
            refVideos: parseRefArray(item.reference_videos),
            refAudio: parseRefArray(item.reference_audio),
            createdAt: item.created_at,
            expired: item.expired,
            source: 'server',
            serverIds: [item.id],
          });
        });
      }

      // 映射图片记录（按 request_id 分组合并多图）
      if (imageRes.status === 'fulfilled' && imageRes.value?.items) {
        const seenRequestIds = new Set();
        imageRes.value.items.forEach(item => {
          const rid = item.request_id || String(item.id);
          if (seenRequestIds.has(rid)) return;
          seenRequestIds.add(rid);
          const grouped = imageRes.value.items
            .filter(i => (i.request_id || String(i.id)) === rid && i.image_url)
            .sort((a, b) => (a.image_index || 0) - (b.image_index || 0));
          const urls = grouped.map(i => i.image_url);
          // 如果该请求下没有成功图片（全是失败的），从原列表取第一个展示状态
          const allOfReq = imageRes.value.items.filter(i => (i.request_id || String(i.id)) === rid);
          const firstItem = grouped.length > 0 ? grouped[0] : allOfReq[0] || item;
          let imgStatus = firstItem.status || 'active';
          if (firstItem.expired) imgStatus = 'expired';
          items.push({
            id: `i-${firstItem.id}`,
            type: 'image',
            model: firstItem.model || item.model,
            prompt: firstItem.prompt || item.prompt,
            taskId: null,
            requestId: rid,
            status: imgStatus,
            errorMsg: firstItem.error_msg || item.error_msg || '',
            imageUrls: urls,
            size: firstItem.size || item.size,
            style: firstItem.style || item.style,
            imageCount: urls.length,
            refImages: parseRefArray(firstItem.reference_images || item.reference_images),
            createdAt: firstItem.created_at || item.created_at,
            expired: firstItem.expired,
            source: 'server',
            serverIds: grouped.length > 0 ? grouped.map(g => g.id) : allOfReq.map(g => g.id),
          });
        });
      }

      // 只有真正拿到服务器数据才更新历史（避免网络波动清空历史）
      if (items.length > 0) {
        setHistory(prev => {
          const serverKeys = new Set(items.map(s => s.requestId || s.taskId).filter(Boolean));
          const localOnly = prev.filter(p =>
            p.source === 'local' && !serverKeys.has(p.requestId || p.taskId)
          );
          const merged = [...items, ...localOnly];
          merged.sort((a, b) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
          return merged;
        });
      }
    } catch (e) {
      console.error('refreshServerHistory failed:', e);
    }
  }, []);

  // 页面加载时从数据库拉取历史记录
  useEffect(() => {
    refreshServerHistory();
  }, [refreshServerHistory]);

  // 页面加载后自动恢复对排队/处理中任务的轮询（刷新页面不丢失）
  useEffect(() => {
    const pendingItems = history.filter(h =>
      (h.status === 'queued' || h.status === 'processing' || h.status === 'running' || h.status === 'pending') &&
      (h.taskId || h.requestId)
    );
    pendingItems.forEach(item => {
      if (pollTimersRef.current[item.requestId || item.taskId]) return; // 已在轮询中
      if (item.type === 'video') {
        pollVideoStatus(item, history);
      } else if (item.type === 'image') {
        pollImageStatus(item, history);
      }
    });
  }, [history]);


  // 获取系统所有可用模型，筛选出文本类型用于 {t('ai_prompt_assistant')}
  useEffect(() => {
    const fetchTextModels = async () => {
      try {
        const res = await modelAPI.getAvailable();
        let modelList = [];
        if (Array.isArray(res.data)) {
          modelList = res.data;
        } else if (res.data?.list) {
          modelList = res.data.list;
        } else if (res.data?.models) {
          modelList = res.data.models;
        }
        const texts = modelList.filter(m => m.type === 'text' || m.type === 'chat');
        if (texts.length > 0) {
          setTextModels(texts);
          // 若当前 aiModel 不在列表中，默认选第一个
          if (!texts.find(t => (t.name || t.model_name) === aiModel)) {
            setAiModel(texts[0].name || texts[0].model_name);
          }
        }
      } catch {
        // 获取失败时保留默认硬编码选项，不影响使用
      }
    };
    fetchTextModels();
  }, []); // 只在挂载时执行一次

  useEffect(() => {
    const list = activeTab === 'video' ? videoModels : imageModels;
    if (list.length > 0) {
      const exists = list.find(m => (m.name || m.model_name) === selectedModel);
      if (!exists || !selectedModel) {
        const first = list[0];
        const name = first.name || first.model_name;
        setSelectedModel(name);
      }
    }
  }, [activeTab, models]);

  // 切换模型时重置参数
  useEffect(() => {
    if (currentFeat && activeTab === 'video') {
      const mid = Math.ceil((currentFeat.durationRange[0] + currentFeat.durationRange[1]) / 2);
      setDuration(mid);
      // 模型不支持全能参考时自动切换到首尾帧模式
      if (currentFeat.maxRefImages === 0 && currentFeat.maxRefVideos === 0 && currentFeat.maxRefAudio === 0) {
        if (videoMode === 'full_reference') setVideoMode('start_end_frames');
        setRefImages([]); setReferenceVideos([]); setReferenceAudio([]);
      }
    }
  }, [selectedModel, activeTab]);

  const fetchModels = async () => {
    try {
      const res = await api.get('/models');
      if (res.code === 0) {
        setModels(res.data?.list || res.data || []);
      } else {
        setFallbackModels();
      }
    } catch {
      setFallbackModels();
    }
  };

  const setFallbackModels = () => {
    setModels([
      { name: 'seedance-2.0', model_name: 'seedance-2.0', display_name: 'Seedance 2.0 Flagship', type: 'video', provider: 'xrtoken' },
      { name: 'seedance-2.0-fast', model_name: 'seedance-2.0-fast', display_name: 'Seedance 2.0 Fast', type: 'video', provider: 'xrtoken' },
      { name: 'seedance-2.0-idle', model_name: 'seedance-2.0-idle', display_name: 'Seedance 2.0 Idle 超分', type: 'video', provider: 'xrtoken' },
      { name: 'seedance-2.0-fast-idle', model_name: 'seedance-2.0-fast-idle', display_name: 'Seedance 2.0 Fast Idle 超分', type: 'video', provider: 'xrtoken' },
      { name: 'seedance-1.5-pro', model_name: 'seedance-1.5-pro', display_name: 'Seedance 1.5 Pro', type: 'video', provider: 'xrtoken' },
      { name: 'seedream-4.0', model_name: 'seedream-4.0', display_name: 'Seedream 4.0', type: 'image', provider: 'xrtoken' },
      { name: 'seedream-4.5', model_name: 'seedream-4.5', display_name: 'Seedream 4.5 Pro', type: 'image', provider: 'xrtoken' },
      { name: 'seedream-5.0-lite', model_name: 'seedream-5.0-lite', display_name: 'Seedream 5.0 Lite', type: 'image', provider: 'xrtoken' },
    ]);
  };

  // ========== 临时素材库 ==========
  const fetchImageLibrary = async (page = 1, tab = null) => {
    const activeTab = tab || materialTab; // 优先使用传入的 tab，避免闭包问题
    setImageLibraryLoading(true);
    try {
      if (activeTab === 'image') {
        const res = await multimodalAPI.getImageHistory(page, 20);
        // 过滤掉失败的记录，素材库只展示成功生成的有效素材
        const validItems = (res.items || []).filter(item => item.status !== 'failed' && item.status !== 'error' && item.image_url);
        setImageLibraryItems(validItems.map(item => ({ ...item, _type: 'image' })));
        setImageLibraryTotal(res.total || 0);
        setImageLibraryPage(page);
      } else {
        // 视频素材：调用服务端 API（从 MySQL video_records 表获取）
        const res = await multimodalAPI.getVideoHistory(page, 20);
        // 过滤掉失败的记录，素材库只展示成功生成的有效素材
        const validItems = (res.items || []).filter(item => item.status !== 'failed' && item.status !== 'error' && item.video_url);
        setImageLibraryItems(validItems.map(item => ({ ...item, _type: 'video', image_url: null, videoUrl: item.video_url })));
        setImageLibraryTotal(res.total || 0);
        setImageLibraryPage(page);
      }
    } catch (e) {
      message.error(t('fetch_library_failed') + ': ' + (e?.message || t('network_error')));
    } finally {
      setImageLibraryLoading(false);
    }
  };

  const openImageLibrary = (target) => {
    setImageLibraryTarget(target);
    setImageLibraryOpen(true);
    setSelectedMaterialId(null); // 重置选中状态
    // 根据 target 类型推断 tab
    const tab = target === 'referenceVideos' ? 'video' : 'image';
    setMaterialTab(tab);
    fetchImageLibrary(1, tab); // 显式传入 tab，避免 React 异步状态闭包问题
  };

  const selectImageFromLibrary = (record) => {
    setSelectedMaterialId(record.id); // 选中反馈
    if (record._type === 'video') {
      const vidObj = { url: record.video_url, name: `seedance_${record.id}`, id: record.id, type: 'video', videoUrl: record.video_url };
      if (imageLibraryTarget === 'referenceVideos') {
        setReferenceVideos(prev => [...prev, vidObj]);
      } else {
        setReferenceVideos(prev => [...prev, vidObj]);
      }
    } else {
      const imgObj = { url: record.image_url, name: `seedream_${record.id}` };
      if (imageLibraryTarget === 'refImages') {
        setRefImages(prev => [...prev, imgObj]);
      } else if (imageLibraryTarget === 'firstFrame') {
        setFirstFrameImage(imgObj);
      } else if (imageLibraryTarget === 'lastFrame') {
        setLastFrameImage(imgObj);
      } else if (imageLibraryTarget.startsWith('multiframe_')) {
        const idx = parseInt(imageLibraryTarget.split('_')[1]);
        setMultiframes(prev => prev.map((f, i) => i === idx ? { ...f, imageUrl: imgObj } : f));
      }
    }
    // 延迟关闭，让用户看到选中反馈
    setTimeout(() => {
      setImageLibraryOpen(false);
      setSelectedMaterialId(null);
    }, 200);
  };

  // ========== 人物素材库管理 ==========

  const openAssetManager = () => {
    setAssetModalOpen(true);
    setAssetTab('works');
    fetchAssetGroups();
  };

  
  const [userIdReady, setUserIdReady] = useState(false);
  useEffect(() => { userAPI.getInfo().then(res => { setUserId(res?.data?.id || ''); setUserIdReady(true); }).catch(() => setUserIdReady(true)); }, []);

  // ========== 素材库管理 ==========
  const fetchAssetGroups = async () => {
    setAssetLoading(true);
    try {
      const [aigcRes, rpRes] = await Promise.all([
        multimodalAPI.listAssetGroups('aigc', userId),
        multimodalAPI.listAssetGroups('real_person', userId),
      ]);
      const aigcItems = (aigcRes.Result && aigcRes.Result.Items) || aigcRes.Items || aigcRes.items || aigcRes.data || [];
      const rpItems = (rpRes.Result && rpRes.Result.Items) || rpRes.Items || rpRes.items || rpRes.data || [];
      setAssetGroups([...aigcItems, ...rpItems]);
    } catch (e) {}
    setAssetLoading(false);
  };
  const fetchAssets = async (groupId) => {
    if (!groupId) return;
    setAssetLoading(true);
    const grp = assetGroups.find(g => (g.Id || g.id) === groupId);
	    let grpType = grp ? (grp.Type || grp.type || '') : '';
	    if (!grpType && grp && (grp.GroupType || grp.group_type) === 'LivenessFace') grpType = 'real_person';
	    if (!grpType && grp) grpType = 'aigc';
	    try { const res = await multimodalAPI.listAssets(groupId, userId, grpType); let serverItems = (res.Result && res.Result.Items) || res.Items || res.items || res.data || []; if (serverItems.length === 0 && userId) { const res2 = await multimodalAPI.listAssets(groupId, '', grpType); serverItems = (res2.Result && res2.Result.Items) || res2.Items || res2.items || res2.data || []; } setAssetList(prev => { const serverIds = new Set(serverItems.map(i => i.Id || i.id)); const processing = prev.filter(i => ((i.Status || i.status) === 'Processing') && !serverIds.has(i.Id || i.id)); return [...serverItems, ...processing]; }); } catch (e) {}
    setAssetLoading(false);
  };
  const handleCreateAssetGroup = async () => {
    if (!newGroupName.trim()) { message.warning(t('enter_group_name')); return; }
    try {
      const grpType = assetTab === 'realperson' ? 'real_person' : (assetTab === 'aigc' ? 'aigc' : '');
	    const res = await multimodalAPI.createAssetGroup(newGroupName.trim(), newGroupDesc.trim(), userId, grpType);
      if (res.error) { message.error(res.error); }
      else { message.success(t('group_created_success')); setNewGroupName(''); setNewGroupDesc(''); setGroupModalOpen(false); setTimeout(() => fetchAssetGroups(), 500); }
    } catch (e) { message.error(e.message || ''); }
  };
  const handleRenameGroup = async (groupId, newName) => {
    if (!newName.trim()) return;
    try { const res = await multimodalAPI.createAssetGroup(newName.trim()); if (!res.error) { fetchAssetGroups(); } } catch (e) {}
  };
  const handleCreateAsset = async (url) => {
    const assetUrl = url || newAssetUrl.trim();
    if (!assetUrl || !selectedAssetGroupId) { message.warning('请选择分组并输入素材URL'); return; }
    try {
      const res = await multimodalAPI.createAsset(selectedAssetGroupId, assetUrl, newAssetType, newAssetName.trim(), userId);
      if (res.error) { message.error(res.error); }
      else { message.success(t('asset_submitted')); setNewAssetUrl(''); setNewAssetName(''); const newId = (res.Result && res.Result.Id) || res.id; if (newId) { setAssetList(prev => [{ Id: newId, Name: newAssetName.trim() || 'untitled', Status: 'Processing', URL: assetUrl }, ...prev]); } fetchAssets(selectedAssetGroupId); }
    } catch (e) { message.error(e.message || ''); }
  };
  const handleFileUpload = async () => {
    if (!uploadFile) return;
    setAssetLoading(true);
    try {
      const res = await multimodalAPI.uploadFile(uploadFile);
      if (res.url || res.URL) { setNewAssetUrl(res.url || res.URL); message.success(t('upload_success')); handleCreateAsset(res.url || res.URL); }
      else if (res.error) { message.error(res.error); }
    } catch (e) { message.error(e.message || ''); }
    setAssetLoading(false);
  };
  const addAssetAsReference = (assetId, assetUrl, assetName, assetType) => {
    const label = assetName || assetId;
    // 请求用 asset:// 格式，XRToken 内部解析；显示用真实 URL
    const ref = 'asset://' + assetId;
    const displayUrl = assetUrl || '';
    const isVideo = assetType === 'Video' || assetType === 'video';
    if (isVideo) {
      setReferenceVideos(prev => [...prev, { url: ref, displayUrl, name: label, isAsset: true, type: 'video' }]);
    } else {
      setRefImages(prev => [...prev, { url: ref, displayUrl, name: label, isAsset: true }]);
    }
    message.success(t('referenced') + ': ' + label);
  };
  const [convertItem, setConvertItem] = useState(null);
  const [convertGroupId, setConvertGroupId] = useState('');
  const [convertName, setConvertName] = useState('');
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const openConvertModal = async (item) => {
    try {
      const [aigcRes] = await Promise.all([multimodalAPI.listAssetGroups('aigc', userId)]);
      const aigcItems = (aigcRes.Result && aigcRes.Result.Items) || aigcRes.Items || aigcRes.items || aigcRes.data || [];
      if (aigcItems.length === 0) { message.warning(t('please_create_aigc_group_first')); return; }
      // 更新 assetGroups 确保 Select 能显示组名而非 ID
      setAssetGroups(prev => { const rp = prev.filter(g => (g.GroupType || g.group_type) === 'LivenessFace'); return [...aigcItems, ...rp]; });
      setConvertItem(item);
      setConvertGroupId(aigcItems[0].Id || aigcItems[0].id);
      setConvertName((item.prompt || '').substring(0, 40));
      setConvertModalOpen(true);
    } catch (e) { message.warning(t('fetch_asset_group_failed')); }
  };
  const handleConvertConfirm = () => {
    if (!convertItem || !convertGroupId) return;
    const isVideo = convertItem.type === 'video' || convertItem._type === 'video';
    const src = isVideo ? (convertItem.videoUrl || convertItem.video_url) : (convertItem.imageUrls?.[0] || convertItem.image_url);
    if (!src) { message.warning(t('no_asset_url_to_convert')); return; }
    multimodalAPI.createAsset(convertGroupId, src, isVideo ? 'Video' : 'Image', convertName.trim() || 'untitled', userId)
      .then(res => { if (res.error) message.error(res.error); else { message.success(t('converted_to_asset_group')); setConvertModalOpen(false); } }).catch(() => {});
  };
  const checkFaceConsent = async () => {
    try { const res = await multimodalAPI.getFaceConsent(); setFaceConsent(res); return res.valid === true; }
    catch (e) { return false; }
  };
  const startLivenessFlow = async () => {
    if (!userId) { message.warning('Loading user info, please try again in a moment...'); return; }
    setLivenessState('consent');
    const consent = await multimodalAPI.getFaceConsent(userId).catch(() => null);
    if (!consent?.valid && consent?.need_versions) {
      await multimodalAPI.upsertFaceConsent({ policy_version: consent.need_versions.policy, volc_rule_version: consent.need_versions.volc }).catch(() => {});
    }
    const callbackUrl = window.location.origin + '/liveness-callback';
    const session = await multimodalAPI.createValidateSession({ external_user_id: userId || undefined, callback_url: callbackUrl }).catch(() => null);
    if (!session?.Result?.QRCodeDataURL) { message.error(t('liveness_verify_failed')); setLivenessState('idle'); return; }
    setLivenessData(session.Result); setLivenessState('qrcode');
    const bytedToken = session.Result.BytedToken;
    const startPolling = () => {
      let attempts = 0; const maxAttempts = 38;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const result = await multimodalAPI.getValidateResult(bytedToken, userId);
          if (!result) return;
          if (result.GroupId && (result.status === 'active' || result.status === 'Active')) {
            clearInterval(poll); setLivenessState('done'); if (userId) { multimodalAPI.updateAssetGroup(result.GroupId, undefined, userId).catch(() => {}); }
            if (userId) { multimodalAPI.updateAssetGroup(result.GroupId, undefined, userId).catch(() => {}); }
            setTimeout(() => fetchAssetGroups(), 500);
            return;
          }
          const errMsg = result.ResponseMetadata?.Error?.Message || result.error || '';
          if (errMsg && !errMsg.toLowerCase().includes('not found') && !errMsg.toLowerCase().includes('pending') && !errMsg.includes('处理中') && !errMsg.includes('等待')) {
            clearInterval(poll); setLivenessState('idle'); setLivenessData(null); message.error(t('verification_failed') + ': ' + errMsg); return;
          }
          if (attempts >= maxAttempts) { clearInterval(poll); setLivenessState('timeout'); setLivenessData(null); }
        } catch (e) {}
      }, 3000);
      livenessTimerRef.current = poll;
    };
    const delayTimer = setTimeout(startPolling, 8000);
    livenessTimeoutRef.current = delayTimer;
  };
  // Format remaining time
  const formatRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return '已过期';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${t('remaining_time')} ${h} ${t('hours_short')} ${m} ${t('minutes_short')}`;
    return `${t('remaining_time')} ${m} ${t('minutes_short')}`;
  };

  // ========== {t('ai_prompt_assistant')}（弹窗模式） ==========
  const generateAIPrompt = async () => {
    const intent = aiModalIntent.trim();
    if (!intent) { message.warning('请描述你想要生成的内容'); return; }
    
    setAiGenerating(true);
    setAiGeneratedGroups([]);
    try {
      const modeDescriptions = {
        full_reference: 'Full reference mode: upload images, videos, audio as reference materials for AI to generate videos.',
        start_end_frames: 'Start/End frame mode: provide first and last frame images, AI generates the intermediate video.',
        smart_multiframe: 'Smart multi-frame mode: customize multiple keyframe images and transition descriptions for frame-by-frame video generation.',
      };
      
      const modeDesc = activeTab === 'video' ? (modeDescriptions[videoMode] || modeDescriptions.full_reference) : 'Image generation mode: use Seedream model to generate images.';
      const count = aiGroupCount || 2;
      
      // 收集当前参数上下文以优化提示词
      const imageContext = activeTab === 'image' ?
        `当前画幅：${imageSize || '1024x1024'}，输出数量：${imageCount || 1} 张` : '';
      const videoContext = activeTab === 'video' ?
        `当前比例：${aspectRatio || '16:9'}，时长：${duration || 5}秒` : '';

      const systemPrompt = (activeTab === 'video'
        ? `你是一个专业的AI视频生成提示词工程师。${modeDesc}
请根据用户的意图，生成 ${count} 组风格截然不同的高质量视频提示词（Prompt）。${videoContext}

每组提示词必须严格遵循不同的创意方向（从以下方向中任选，确保${count}组之间风格迥异）：
- 电影感叙事：强烈的戏剧性运镜、电影级光影、慢动作情感渲染
- 快节奏动感：高速运动、快速切换景别、强烈视觉冲击、动感转场
- 梦幻唯美：柔光滤镜、诗意慢镜头、空灵感氛围、虚化过渡
- 科幻未来感：赛博朋克色调、霓虹光影、几何构图、机械美学
- 写实纪录片：手持镜头感、自然光线、纪实风格、微观/宏观视角
- 艺术实验：抽象构图、色彩分离、倒放/变速、非常规视角

视频提示词核心要素（每组至少包含其中4项）：
- 动作描述：主体的具体运动方式、速度、方向、节奏
- 镜头语言：运镜方式（推拉摇移跟升降）、景别（特写/中景/远景）、焦点变化
- 光影动态：光源变化方向、阴影移动轨迹、色调从暖到冷的过渡
- 时间维度：画面变化的时间线、渐变/突变的时机、先后顺序
- 氛围营造：整体情绪走向定调、色彩倾向锚点
- 长度控制在120-300个英文单词

通用要求：
1. 使用英文描述（英文提示词效果最佳），每组提示词风格必须明显不同
2. 包含具体的视觉细节和量化描述（如"slow push-in from wide to close-up, Rembrandt lighting"），不要空泛描述
3. 每组附一个简短的英文标题（如Cyberpunk Cat, Watercolor Garden）（如Cyberpunk Cat, Watercolor Garden），帮助快速识别风格差异

返回严格的 JSON 数组格式：
[{"title": "风格标题", "prompt": "英文提示词"}]

只返回JSON数组，不要任何其他文字。`

        : `你是一个专业的AI图片生成提示词工程师。图片{t('video_mode_label')}：用户使用 Seedream 模型生成高质量图片。${imageContext}
请根据用户的意图，生成 ${count} 组风格截然不同的高质量专业提示词（Prompt）。

Seedream 支持 Exclude（排除词汇）功能，请为每组生成对应的 negative_prompt。

每组提示词必须严格遵循不同的创意方向（从以下方向中任选，确保${count}组之间风格迥异）：
- 写实摄影：真实光影、35mm胶片质感、自然肤色、环境光反射
- 艺术插画：手绘笔触、水彩/油画/版画质感、艺术化色彩
- 电影海报：戏剧性灯光、强对比度、电影级景深、人物英雄化
- 3D渲染：PBR材质、全局光照、毛发细节、体积光雾
- 极简风格：大面积留白、几何构图、低饱和度、干净线条
- 复古美学：特定年代风格（50s/80s/90s）、胶片颗粒、褪色效果

图片提示词核心要素（每组至少包含其中3项）：
- 画面构图：主体位置、空间层次、视觉引导线、画幅适配
- 光影色彩：光源类型（主光/补光/轮廓光）、色温、色调统一性、对比度
- 风格锚点：必须明确指定艺术风格关键词（如"oil painting, cinematic lighting, golden hour"）
- 细节质感：材质纹理（金属/布料/皮肤/玻璃）、环境氛围、人物表情/姿态
- 长度控制在80-250个英文单词`) + `

通用要求：
1. 使用英文描述（英文提示词效果最佳），每组提示词风格必须明显不同
2. 包含具体的视觉细节和量化描述（如"2/3 rule of thirds composition, Rembrandt lighting"），不要空泛描述
3. 每组必须提供 negative_prompt，列出应该避免的元素
4. 每组附一个简短的英文标题（如Cyberpunk Cat, Watercolor Garden）（如Cyberpunk Cat, Watercolor Garden），帮助快速识别风格差异

返回严格的 JSON 数组格式：
[{"title": "风格标题", "prompt": "英文提示词", "negative_prompt": "避免元素"}]

只返回JSON数组，不要任何其他文字。`;

      const res = await fetch(getFullApiUrl() + '/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('user_token'),
        },
        body: JSON.stringify({
          model: aiModel,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `我想生成${activeTab === 'video' ? '一个视频' : '一张图片'}：${intent}` },
          ],
          max_tokens: 2000,
          temperature: 0.7,
        }),
      });
      
      if (!res.ok) throw new Error(t('ai_request_failed'));
      const data = await res.json();
      const content = data?.choices?.[0]?.message?.content || '';
      
      // 解析 JSON 响应
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setAiGeneratedGroups(parsed.map(g => ({
              prompt: g.prompt || '',
              negative_prompt: g.negative_prompt || '',
              title: g.title || `方案 ${parsed.indexOf(g) + 1}`,
            })));
          } else {
            // 回退：尝试单个对象
            const objMatch = content.match(/\{[\s\S]*\}/);
            if (objMatch) {
              const obj = JSON.parse(objMatch[0]);
              setAiGeneratedGroups([{ prompt: obj.prompt || '', negative_prompt: obj.negative_prompt || '', title: '方案 1' }]);
            }
          }
        } else {
          // 无法解析 JSON，直接用原文
          setAiGeneratedGroups([{ prompt: content.trim(), negative_prompt: '', title: '方案 1' }]);
        }
      } catch {
        setAiGeneratedGroups([{ prompt: content.trim(), negative_prompt: '', title: '方案 1' }]);
      }
      
      message.success(t('prompts_generated').replace('{count}', aiGeneratedGroups.length || count));
    } catch (e) {
      const errMsg = e?.message || '生成失败';
      message.error('提示词生成失败: ' + errMsg);
    } finally {
      setAiGenerating(false);
    }
  };

  // ========== 视频API响应格式统一提取工具 ==========
  // 从各种视频API响应中提取 status（顶层 status → data.status）
  const extractVideoStatus = (res) => {
    if (!res) return 'processing';
    if (res.status) return res.status;
    if (res.data?.status) return res.data.status;
    return 'processing';
  };
  // 从各种视频API响应中提取 video_url（顶层 video_url → output.video_url → data.result.output.video_url）
  const extractVideoUrl = (res) => {
    if (!res) return '';
    if (res.video_url) return res.video_url;
    if (res.output?.video_url) return res.output.video_url;
    if (res.data?.result?.output?.video_url) return res.data.result.output.video_url;
    return '';
  };

  // ========== 生成视频 ==========
  const generateVideo = async () => {
    if (!prompt.trim() && videoMode !== 'smart_multiframe') { message.warning(t('input_video_desc')); return; }
    if (!selectedModel) { message.warning(t('select_model_first')); return; }
    if (!currentFeat) { message.warning(t('unknown_model_version')); return; }

    // 模式校验
    if (videoMode === 'start_end_frames') {
      if (!firstFrameImage) { message.warning('请上传首帧图片'); return; }
    }
    if (videoMode === 'smart_multiframe') {
      if (multiframes.length < 2) { message.warning(t('at_least_2_keyframes')); return; }
    }

    setGenerating(true);
    try {
      const reqBody = {
        model: selectedModel,
        prompt: prompt.trim(),
        duration,
        resolution,
        aspect_ratio: aspectRatio,
        watermark,
        video_mode: videoMode,
      };
      if (!useRandomSeed && videoSeed != null) reqBody.seed = parseInt(videoSeed);
      if (videoNegativePrompt.trim()) reqBody.negative_prompt = videoNegativePrompt.trim();
      if (cameraFixed) reqBody.camera_fixed = true;
      if (returnLastFrame && videoMode === 'start_end_frames') reqBody.return_last_frame = true;

      // 模式特定参数
      if (videoMode === 'full_reference') {
        if (refImages.length > 0) reqBody.reference_images = refImages.map(img => img.url || img);
        if (referenceVideos.length > 0) reqBody.reference_videos = referenceVideos.map(v => v.url || v);
        if (referenceAudio.length > 0) reqBody.reference_audio = referenceAudio.map(a => a.url || a);
        if (generateAudio) reqBody.generate_audio = true;
      } else if (videoMode === 'start_end_frames') {
        if (firstFrameImage) reqBody.first_frame_image = firstFrameImage.url || firstFrameImage;
        if (lastFrameImage) reqBody.last_frame_image = lastFrameImage.url || lastFrameImage;
        if (generateAudio) reqBody.generate_audio = true;
      } else if (videoMode === 'smart_multiframe') {
        const parts = [];
        const od = openingDuration || 2;
        if (openingText.trim()) parts.push("开场(" + od + "s)：" + openingText.trim());
        else parts.push("开场(" + od + "s)");
        multiframes.forEach((mf, i) => {
          const td = mf.transitionDuration || 2;
          const tp = mf.transitionPrompt?.trim();
          if (i < multiframes.length - 1) {
            if (tp) parts.push("第" + (i+1) + "帧后(" + td + "s)：" + tp);
            else parts.push("第" + (i+1) + "帧后(" + td + "s)");
          } else if (tp) {
            parts.push("第" + (i+1) + "帧后(" + td + "s)：" + tp);
          }
        });
        reqBody.prompt = parts.join("。");
        reqBody.duration = od + multiframes.reduce((s, mf) => s + (mf.transitionDuration || 2), 0);
        reqBody.keyframes = multiframes.map(mf => ({ image_url: mf.imageUrl?.url || mf.imageUrl }));
      }
      if (returnLastFrame && videoMode === 'start_end_frames') {
        reqBody.return_last_frame = true;
      }

      const res = await api.post('/v1/videos/generations', reqBody);
      const taskId = res?.id || res?.task_id || '';

      if (!taskId) {
        throw new Error('No task_id returned');
      }

      // 创建乐观本地记录，立即显示在历史列表
      const record = {
        id: `local-${taskId}`,
        type: 'video',
        model: selectedModel,
        prompt: prompt.trim(),
        taskId,
        status: 'queued',
        videoUrl: '',
        params: { duration, resolution, aspectRatio, watermark, seed: useRandomSeed ? null : videoSeed, videoMode },
        refImages: refImages.map(img => ({ url: img.url || img, displayUrl: img.displayUrl || '' })),
        refVideos: referenceVideos.map(v => ({ url: v.url || v, displayUrl: v.displayUrl || '' })),
        refAudio: referenceAudio.map(a => ({ url: a.url || a, displayUrl: a.displayUrl || '' })),
        firstFrameImage: firstFrameImage || null,
        lastFrameImage: lastFrameImage || null,
        keyframes: multiframes.length > 0 ? multiframes.map(mf => ({ imageUrl: mf.imageUrl, transitionPrompt: mf.transitionPrompt })) : null,
        createdAt: new Date().toISOString(),
        source: 'local',
      };

      setHistory(prev => [record, ...prev]);
      resetVideoForm();
      setActiveTab('video');
      localStorage.setItem('creativeTab', 'video');
      message.info(t('video_task_submitted'));

    } catch (e) {
      resetVideoForm();
      showError(t('generation_failed_video'), e);
    } finally {
      setGenerating(false);
    }
  };

  const pollVideoStatus = async (record, currentHistory) => {
    if (!record.taskId) return;
    const maxAttempts = 240; // 20${t('minutes_short')} @5s间隔
    let attempts = 0;

    const poll = () => {
      if (attempts >= maxAttempts) {
        setHistory(prev => prev.map(h => (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status: 'timeout' } : h));
        return;
      }
      attempts++;

      pollTimersRef.current[record.requestId || record.taskId] = setTimeout(async () => {
        try {
          const res = await api.get(`/v1/videos/generations/${record.taskId}?model=${encodeURIComponent(record.model)}`);
          // 兼容多种视频API响应格式
          const status = extractVideoStatus(res);
          const videoUrl = extractVideoUrl(res) || record.videoUrl;

          setHistory(prev => prev.map(h =>
            (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status, videoUrl } : h
          ));

          // xrtoken 终态: succeeded | failed | expired | cancelled
          if (status === 'succeeded') {
            // 计算耗时（从创建到完成）
            if (videoUrl) record.videoUrl = videoUrl;
            message.success(t('generation_complete_video_msg'));
            bindServerId({ taskId: record.taskId, type: 'video' }, record.id, false);
          } else if (status === 'failed') {
            delete pollTimersRef.current[(record.requestId || record.taskId)];
            const errMsg = res?.error?.message || t('unknown_error');
            showError(t('generation_failed_video'), errMsg);
            setHistory(prev => prev.map(h => (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status: 'failed', errorMsg: errMsg } : h));
            bindServerId({ taskId: record.taskId, type: 'video' }, record.id, true);
          } else if (status === 'expired' || status === 'cancelled') {
            const errMsg = status === 'expired' ? t('task_expired') : t('task_cancelled');
            message.warning({ content: t('generation_failed_video') + ': ' + errMsg, style: { textAlign: 'left' } });
            setHistory(prev => prev.map(h => (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status, errorMsg: errMsg } : h));
            bindServerId({ taskId: record.taskId, type: 'video' }, record.id, true);
          } else if (status === 'queued' || status === 'processing') {
            // 还在进行中，继续轮询
            poll();
          } else {
            // 未知状态，继续轮询
            poll();
          }
        } catch {
          poll();
        }
      }, 5000);
    };

    setTimeout(poll, 3000);
  };

  // ========== 生成图片（异步模式：先创建历史记录，再提交任务，然后轮询结果） ==========
  const generateImage = async () => {
    if (!prompt.trim()) { message.warning(t('input_image_desc')); return; }
    if (!selectedModel) { message.warning(t('select_model_first')); return; }
    if (!currentFeat) { message.warning(t('unknown_model_version')); return; }

    setGenerating(true);
    try {
      const reqBody = {
        model: selectedModel,
        prompt: prompt.trim(),
        size: imageSize,
        n: imageCount,
      };
      if (currentFeat?.async) {
        reqBody.quality = imageQuality;
      } else {
        reqBody.style = imageStyle;
        reqBody.guidance_scale = guidanceScale;
      }
      if (negativePrompt.trim()) reqBody.negative_prompt = negativePrompt.trim();
      if (!useRandomImageSeed && imageSeed != null) reqBody.seed = parseInt(imageSeed);
      if (refImages.length > 0) {
        reqBody.reference_images = refImages.map(img => img.displayUrl || img.url || img);
      }

      const res = await api.post('/v1/images/generations', reqBody);
      const requestId = res?.request_id || '';

      if (!requestId) {
        throw new Error('No request_id returned');
      }

      // 创建乐观本地记录，立即显示在历史列表（标准做法：即时反馈）
      const record = {
        id: `local-${requestId}`,
        type: 'image',
        model: selectedModel,
        prompt: prompt.trim(),
        requestId,
        imageUrls: [],
        status: 'queued',
        params: { size: imageSize, count: imageCount, ...(currentFeat?.async ? { quality: imageQuality } : { style: imageStyle, guidanceScale }), seed: useRandomImageSeed ? null : imageSeed },
        refImages: refImages.map(img => ({ url: img.url || img, displayUrl: img.displayUrl || '' })),
        createdAt: new Date().toISOString(),
        source: 'local',
      };

      setHistory(prev => [record, ...prev]);
      setPrompt('');
      setNegativePrompt('');
      setRefImages([]);
      setActiveTab('image');
      localStorage.setItem('creativeTab', 'image');
      message.info(t('image_task_submitted'));
    } catch (e) {
      setPrompt('');
      setNegativePrompt('');
      setRefImages([]);
      showError(t('generation_failed_image'), e);
    } finally {
      setGenerating(false);
    }
  };

  // ========== 轮询图片生成状态 ==========
  const pollImageStatus = async (record, currentHistory) => {
    if (!record.requestId) return;
    const maxAttempts = 300; // ~8$${t('minutes_short')} @5s间隔
    let attempts = 0;

    const poll = () => {
      if (attempts >= maxAttempts) {
        setHistory(prev => prev.map(h => (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status: 'timeout' } : h));
        return;
      }
      attempts++;

      pollTimersRef.current[record.requestId || record.taskId] = setTimeout(async () => {
        try {
          const res = await api.get(`/v1/images/generations/${record.requestId}`);
          const status = res?.status || 'processing';

          // 提取图片URL
          let imageUrls = [];
          if (res?.data && Array.isArray(res.data)) {
            imageUrls = res.data.map(d => (typeof d === 'string') ? d : (d?.url || '')).filter(Boolean);
          }

          setHistory(prev => prev.map(h =>
            (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status, imageUrls } : h
          ));

          if (status === 'completed') {
            delete pollTimersRef.current[(record.requestId || record.taskId)];
            if (imageUrls.length > 0) {
              setPreviewImageIndex(0);
              setPreviewData({ type: 'image', urls: imageUrls, prompt: record.prompt, record: { ...record, status, imageUrls } });
              setPreviewVisible(true);
            } else {
              message.warning({ content: t('generation_failed_image_no_url'), style: { textAlign: 'left' } });
            }
            bindServerId({ requestId: record.requestId, type: 'image' }, record.id, false);
          } else if (status === 'failed') {
            delete pollTimersRef.current[(record.requestId || record.taskId)];
            const errMsg = res?.error || t('unknown_error');
            setHistory(prev => prev.map(h => (h.requestId || h.taskId) === (record.requestId || record.taskId) ? { ...h, status: 'failed', errorMsg: errMsg } : h));
            bindServerId({ requestId: record.requestId, type: 'image' }, record.id, true);
          } else if (status === 'queued' || status === 'processing') {
            poll();
          } else {
            poll();
          }
        } catch {
          poll();
        }
      }, 3000);
    };

    setTimeout(poll, 2000);
  };

  // ========== 手动检查图片状态 ==========
  const manualCheckImage = async (item) => {
    if (!item.requestId) return;
    const s = message.loading(t('checking_status'), 0);
    try {
      const res = await api.get(`/v1/images/generations/${item.requestId}`);
      const status = res?.status || 'processing';
      let imageUrls = [];
      if (res?.data && Array.isArray(res.data)) {
        imageUrls = res.data.map(d => (typeof d === 'string') ? d : (d?.url || '')).filter(Boolean);
      }
      const updated = history.map(h =>
        h.id === item.id ? { ...h, status, imageUrls } : h
      );
      setHistory(updated);
      if (status === 'completed' && imageUrls.length > 0) {
        message.success(t('generation_complete_image_msg'));
        setPreviewImageIndex(0);
        setPreviewData({ type: 'image', urls: imageUrls, prompt: item.prompt, record: { ...item, status, imageUrls } });
        setPreviewVisible(true);
      } else if (status === 'failed') {
        showError(t('generation_failed_image'), res?.error || t('unknown_error'));
      } else if (status === 'queued' || status === 'processing') {
        message.info(t('image_task_submitted') + ' - ' + status);
        // 启动轮询
        pollImageStatus({ ...item, status, imageUrls }, updated);
      } else {
        message.info(t('task_status') + ': ' + status);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || t('check_failed');
      message.error({ content: t('check_failed') + ': ' + errMsg, style: { textAlign: 'left' } });
    } finally {
      s();
    }
  };

  const handleGenerate = () => {
    if (activeTab === 'video') generateVideo();
    else generateImage();
  };

  // 提取用户可读的错误消息（支持多种格式）
  const extractErrorMsg = (data) => {
    if (!data) return null;
    // 1. error.message（常见：{"error":{"code":"...","message":"...","param":"","type":"..."}}）
    if (data.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    // 2. error 为字符串
    if (typeof data.error === 'string') return data.error;
    // 3. message 字段
    if (typeof data.message === 'string') return data.message;
    return null;
  };
  const showError = (title, err) => {
    let msg = '';
    // 尝试从 axios error 提取
    if (err?.serverMessage) { msg = err.serverMessage; }
    else if (typeof err === 'string') { msg = err; }
    else {
      const d = err?.response?.data || err;
      const m = typeof d === 'object' ? extractErrorMsg(d) : null;
      msg = m || (typeof d === 'string' ? d : (err?.message || t('network_error')));
    }
    message.error({ content: title + ': ' + msg, style: { textAlign: 'left' } });
  };

  // 查询服务器历史记录，找到 taskId/requestId 对应的 DB id，绑定到临时记录上确保删除能同步数据库
  const bindServerId = async ({ taskId, requestId, type }, tempRecordId, silent) => {
    if (!taskId && !requestId) return;
    try {
      const apiFn = type === 'video' ? multimodalAPI.getVideoHistory : multimodalAPI.getImageHistory;
      const res = await apiFn(1, 50);
      if (!res?.items) return;
      const matches = res.items.filter(i => {
        if (type === 'video') return i.task_id === taskId;
        return i.request_id === requestId;
      });
      if (matches.length > 0) {
        const serverIds = matches.map(m => m.id);
        setHistory(prev => prev.map(h =>
          h.id === tempRecordId ? { ...h, serverIds, source: 'server' } : h
        ));
      }
    } catch { if (!silent) { /* ignore bind errors */ } }
  };

  // ========== 历史管理 ==========
  const deleteHistoryItem = async (id) => {
    const item = history.find(h => h.id === id);
    if (!item) return;

    // 服务端记录：先调用后端 API 从数据库删除（遍历分组内的所有记录 ID）
    if (item.serverIds?.length) {
      try {
        const deletePromises = item.serverIds.map(sid => {
          if (item.type === 'video') {
            return multimodalAPI.deleteVideoHistory(sid).catch(() => {});
          } else {
            return multimodalAPI.deleteImageHistory(sid).catch(() => {});
          }
        });
        await Promise.allSettled(deletePromises);
      } catch {
        // 后端删除失败也不阻塞前端移除
      }
    }

    if (pollTimersRef.current[id]) {
      clearTimeout(pollTimersRef.current[id]);
      delete pollTimersRef.current[id];
    }
    const newHistory = history.filter(h => h.id !== id);
    setHistory(newHistory);
  };

  // 手动检查视频状态
  const manualCheckVideo = async (item) => {
    if (!item.taskId) return;
    const s = message.loading(t('checking_status'), 0);
    try {
      const res = await api.get(`/v1/videos/generations/${item.taskId}?model=${encodeURIComponent(item.model || '')}`);
      const status = extractVideoStatus(res);
      const videoUrl = extractVideoUrl(res) || item.videoUrl;
      const updated = history.map(h =>
        h.id === item.id ? { ...h, status, videoUrl } : h
      );
      setHistory(updated);
      if (status === 'succeeded') {
        message.success(t('generation_complete_video_msg'));
      } else if (status === 'failed') {
        const errMsg = res?.error?.message || t('unknown_error');
        showError(t('generation_failed_video'), errMsg);
      } else if (status === 'expired' || status === 'cancelled') {
        message.warning({ content: t('generation_failed_video') + ': ' + (status === 'expired' ? t('task_expired') : t('task_cancelled')), style: { textAlign: 'left' } });
      } else {
        message.info(t('task_status') + ': ' + status);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || t('check_failed');
      message.error({ content: t('check_failed') + ': ' + errMsg, style: { textAlign: 'left' } });
    } finally {
      s();
    }
  };

  const clearHistory = async () => {
    Object.values(pollTimersRef.current).forEach(clearTimeout);
    pollTimersRef.current = {};

    // 只清除当前 tab 类型的历史记录（视频或图片），不影响另一种类型
    const toDelete = history.filter(h => h.type === activeTab && h.serverIds?.length);
    const deletePromises = toDelete.flatMap(h =>
      h.serverIds.map(sid => {
        if (h.type === 'video') {
          return multimodalAPI.deleteVideoHistory(sid).catch(() => {});
        } else {
          return multimodalAPI.deleteImageHistory(sid).catch(() => {});
        }
      })
    );

    if (deletePromises.length > 0) {
      await Promise.allSettled(deletePromises);
    }

    setHistory(prev => prev.filter(h => h.type !== activeTab));
    const typeLabel = t(activeTab === 'video' ? 'video_label' : 'image_label');
    message.success(t('cleared_all_prefix') + typeLabel + t('generation_history'));
  };

  // ========== 下载功能 ==========
  // 通过后端代理下载，绕过浏览器 CORS 限制
  const getProxyDownloadUrl = (sourceUrl) => {
    const base = getFullApiUrl();
    return base + '/v1/download?url=' + encodeURIComponent(sourceUrl);
  };

  // 下载单个文件（图片或视频）- 直接走代理 URL，浏览器原生流式下载，无需等待全量缓冲
  const downloadSingleFile = (url, filename) => {
    const token = localStorage.getItem('user_token');
    const proxyUrl = getProxyDownloadUrl(url) + '&token=' + encodeURIComponent(token);
    const a = document.createElement('a');
    a.href = proxyUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 多张图片打包下载为 ZIP
  const downloadAllAsZip = async (imageUrls, baseFilename) => {
    try {
      const hide = message.loading(t('packaging_zip'), 0);
      const token = localStorage.getItem('user_token');
      const zip = new JSZip();
      const promises = imageUrls.map(async (url, i) => {
        const proxyUrl = getProxyDownloadUrl(url);
        const response = await fetch(proxyUrl, {
          headers: { 'Authorization': 'Bearer ' + token },
        });
        if (!response.ok) {
          throw new Error('Download failed for image ' + (i + 1));
        }
        const blob = await response.blob();
        const ext = getFileExt(url);
        zip.file(baseFilename + '_' + (i + 1) + '.' + ext, blob);
      });
      await Promise.all(promises);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const blobUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = baseFilename + '.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      hide();
      message.success(t('download_success'));
    } catch (err) {
      message.error(t('download_failed'));
    }
  };

  // 判断图片URL是否可下载（排除base64占位图）
  const isDownloadableUrl = (url) => url && (url.startsWith('http://') || url.startsWith('https://'));

  // 从URL提取文件扩展名
  const getFileExt = (url) => {
    const parts = url.split('.').pop() || 'png';
    return parts.split('?')[0];
  };

  const clearPrompt = () => {
    setPrompt('');
    setNegativePrompt('');
    setRefImages([]);
    setFirstFrameImage(null);
    setLastFrameImage(null);
    setMultiframes([]);
    setReferenceVideos([]);
    setReferenceAudio([]);
  };

  // 重置视频表单（提交成功后调用）
  const resetVideoForm = () => {
    setPrompt('');
    setNegativePrompt('');
    setRefImages([]);
    setFirstFrameImage(null);
    setLastFrameImage(null);
    setMultiframes([]);
    setReferenceVideos([]);
    setReferenceAudio([]);
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      Object.values(pollTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const historyFiltered = history.filter(h => h.type === activeTab);

  // ========== 渲染 ==========
  const renderModelCard = (model, type) => {
    const name = model.name || model.model_name;
    const feat = (() => {
      const hc = MODEL_FEATURES[type]?.[name];
      const apiModel = models.find(m => (m.name || m.model_name) === name);
      if (apiModel?.metadata) {
        try {
          const meta = typeof apiModel.metadata === 'string' ? JSON.parse(apiModel.metadata) : apiModel.metadata;
          return { ...hc, ...meta, features: meta.features || hc?.features || [], resolutions: meta.resolutions || hc?.resolutions, durationRange: meta.durationRange || hc?.durationRange, fpsOptions: meta.fpsOptions || hc?.fpsOptions, aspectRatios: meta.aspectRatios || hc?.aspectRatios, maxRefImages: meta.maxRefImages ?? hc?.maxRefImages, maxRefVideos: meta.maxRefVideos ?? hc?.maxRefVideos, maxRefAudio: meta.maxRefAudio ?? hc?.maxRefAudio, maxImages: meta.maxImages ?? hc?.maxImages, styles: meta.styles || hc?.styles };
        } catch(e) {}
      }
      return hc || { features: [], resolutions: [], durationRange: [4,10], fpsOptions: [24], aspectRatios: [] };
    })();
    const isSelected = selectedModel === name;
    return (
      <Card
        key={name}
        hoverable
        size="small"
        style={{
          cursor: 'pointer',
          borderColor: isSelected ? '#1677ff' : undefined,
          background: isSelected ? '#f0f5ff' : '#fff',
          marginBottom: 8,
          transition: 'all .2s',
        }}
        styles={{ body: { padding: '10px 12px' } }}
        onClick={() => setSelectedModel(name)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Space size={4}>
              <Text strong style={{ fontSize: 13 }}>{model.display_name || name}</Text>
              {feat && <Tag color={feat.tierColor} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>{feat.tier}</Tag>}
            </Space>
            {feat && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{feat.desc}</Text>
              </div>
            )}
          </div>
          {isSelected && <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />}
        </div>
        {feat && isSelected && (
          <div style={{ marginTop: 6 }}>
            <Space size={2} wrap>
              {(feat.features || []).map((f, i) => (
                <Tag key={i} color="blue" style={{ fontSize: 10, margin: '1px 0', lineHeight: '16px', padding: '0 4px' }}>{f}</Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>
    );
  };

  const renderStatus = (status) => {
    const cfg = getStatusConfig()[status];
    if (!cfg) return <Tag>{status || t('generating_progress')}</Tag>;
    return <Tag color={cfg.color} icon={cfg.icon}>{cfg.text}</Tag>;
  };

  // ========== 视频模式选择器 + 模式特定区域 ==========
  const renderVideoModeSelector = () => {
    const noFullRef = currentFeat && currentFeat.maxRefImages === 0 && currentFeat.maxRefVideos === 0 && currentFeat.maxRefAudio === 0;
    const modeOptions = [
      {
        value: 'full_reference',
        label: t('mode_full_reference'),
        icon: <PictureOutlined />,
        desc: noFullRef ? t('mode_full_reference_disabled') : t('mode_full_reference_desc'),
        disabled: noFullRef,
      },
      {
        value: 'start_end_frames',
        label: t('mode_start_end_frames'),
        icon: <VideoCameraOutlined />,
        desc: t('mode_start_end_frames_desc')
      },
      {
        value: 'smart_multiframe',
        label: t('mode_smart_multiframe'),
        icon: <BulbOutlined />,
        desc: t('mode_smart_multiframe_desc')
      },
    ];

    return (
      <div style={{ marginBottom: 20 }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>{t('video_mode_label')}</Text>
        <Radio.Group
          value={videoMode}
          onChange={e => {
            setVideoMode(e.target.value);
            // 切换模式时重置相关状态
            if (e.target.value !== 'full_reference') {
              setRefImages([]);
              setReferenceVideos([]);
              setReferenceAudio([]);
            }
            if (e.target.value !== 'start_end_frames') {
              setFirstFrameImage(null);
              setLastFrameImage(null);
            }
            if (e.target.value !== 'smart_multiframe') {
              setMultiframes([]);
            } else if (multiframes.length === 0) {
              setMultiframes([
                { id: Date.now().toString(), imageUrl: null, transitionDuration: 3 },
                { id: (Date.now()+1).toString(), imageUrl: null, transitionDuration: 2 },
              ]);
            }
          }}
          size="small"
          style={{ display: 'flex', gap: 6, width: '100%' }}
        >
          {modeOptions.map(opt => (
            <Radio.Button
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              style={{
                flex: 1, textAlign: 'center', fontSize: 12,
                height: 'auto', padding: '8px 10px',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span style={{ fontSize: 16 }}>{opt.icon}</span>
                <Text strong style={{ fontSize: 11 }}>{opt.label}</Text>
                <Text type="secondary" style={{ fontSize: 9, lineHeight: 1.2 }}>{opt.desc}</Text>
              </div>
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
    );
  };

  // 渲染通用图片上传组件
  const renderImageUpload = (image, setImage, label, maxCount = 1, disabled = false) => (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</Text>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {image ? (
          <div style={{ position: 'relative', width: 72, height: 72 }}>
            <img src={image.url || image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
            <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
              style={{ position: 'absolute', top: -8, right: -8, padding: 0 }}
              onClick={() => setImage(null)} />
          </div>
        ) : (
          <Upload accept="image/*" showUploadList={false} disabled={disabled}
            beforeUpload={(file) => {
              multimodalAPI.uploadFile(file).then(res => {
                const upUrl = res.url || res.URL;
                if (upUrl) setImage({ url: upUrl, name: file.name });
                else message.error(res.error || 'Upload failed');
              }).catch(e => message.error(e.message || ''));
              return false;
            }}>
            <div style={{
              width: 72, height: 72, border: '1px dashed #d9d9d9',
              borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer', color: '#999', fontSize: 11,
              opacity: disabled ? 0.5 : 1,
            }}>
              <Space direction="vertical" size={0} align="center">
                <PlusOutlined />
                <span style={{ fontSize: 10 }}>{t("upload")}</span>
              </Space>
            </div>
          </Upload>
        )}
      </div>
    </div>
  );

  // 渲染多功能上传 (支持图片/视频/音频)
  const renderMultiUpload = (files, setFiles, accept, label, maxCount, icon) => (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
        {icon} {label} {maxCount > 0 && <Text type="secondary" style={{ fontSize: 10 }}>{t("max_count", {count: maxCount})}</Text>}
      </Text>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {files.map((f, i) => (
          <div key={i} style={{ position: 'relative', width: 56, height: 56 }}>
            {(f.type?.startsWith('video') || f.type === 'video') && f.isAsset && (
              <span style={{ position: 'absolute', top: -10, left: -6, background: '#722ed1', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, zIndex: 2, lineHeight: '15px', whiteSpace: 'nowrap' }}>视频{i + 1}</span>
            )}
            {f.type?.startsWith('video') || f.type === 'video' ? (
              <div style={{ width: '100%', height: '100%', background: '#000', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {f.videoUrl ? (
                  <video src={f.videoUrl} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : null}
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: f.videoUrl ? 'rgba(0,0,0,0.25)' : 'transparent',
                }}>
                  <PlayCircleOutlined style={{ fontSize: 20, color: '#fff' }} />
                </div>
              </div>
            ) : f.type?.startsWith('audio') ? (
              <div style={{ width: '100%', height: '100%', background: '#f0f5ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SoundOutlined style={{ fontSize: 20, color: '#1677ff' }} />
              </div>
            ) : (
              <img src={f.url || f} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
            )}
            <Text type="secondary" style={{ fontSize: 9, display: 'block', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 56 }}>
              {f.name?.length > 6 ? f.name.substring(0, 6) + '..' : f.name}
            </Text>
            <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
              style={{ position: 'absolute', top: -8, right: -8, padding: 0, fontSize: 10 }}
              onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
          </div>
        ))}
        {(maxCount === 0 || files.length < maxCount) && (
          <Upload accept={accept} showUploadList={false}
            beforeUpload={(file) => {
              multimodalAPI.uploadFile(file).then(res => {
                const upUrl = res.url || res.URL;
                if (upUrl) setFiles(prev => [...prev, { url: upUrl, name: file.name, type: file.type }]);
                else message.error(res.error || 'Upload failed');
              }).catch(e => message.error(e.message || ''));
              return false;
            }}>
            <div style={{
              width: 56, height: 56, border: '1px dashed #d9d9d9',
              borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#999', fontSize: 10,
            }}>
              <Space direction="vertical" size={0} align="center">
                <PlusOutlined style={{ fontSize: 12 }} />
              </Space>
            </div>
          </Upload>
        )}
      </div>
    </div>
  );

  // 渲染智能多帧编辑器
  // 火山方舟 Seedance 2.0 官方限制：最多 4 个关键帧（首帧 + 2个中间帧 + 尾帧）
  // 转场描述仅在相邻关键帧之间有效（首帧→中间帧→尾帧，最后一帧无转场）
  const MAX_KEYFRAMES = 5;
  const renderMultiframeEditor = () => {
    const totalDuration = (openingDuration || 2) + multiframes.reduce((s, mf) => s + (mf.transitionDuration || 2), 0);
    return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t('total_duration')}: {totalDuration}s | {t('frame_count')}: {multiframes.length}/{MAX_KEYFRAMES}
        </Text>
        <Button size="small" type="dashed" icon={<PlusOutlined />}
          disabled={multiframes.length >= MAX_KEYFRAMES}
          onClick={() => setMultiframes(prev => [...prev, { id: Date.now().toString(), imageUrl: null, transitionDuration: 2 }])}
          style={{ fontSize: 11 }}>
          {t('add_frame')}
        </Button>
      </div>
      <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '8px 10px', marginBottom: 6, border: '1px solid #e8e8e8' }}>
        <Text strong style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>{t('opening_content')}</Text>
        <Space size={6} style={{ width: '100%' }}>
          <Input size="small" value={openingText} onChange={e => setOpeningText(e.target.value)}
            placeholder={t('opening_desc_placeholder')} style={{ flex: 1, minWidth: 180 }} />
          <Space size={2}><Input size="small" type="number" min={1} max={10} value={openingDuration}
            onChange={e => setOpeningDuration(parseInt(e.target.value) || 2)} style={{ width: 52 }} /><Text style={{ fontSize: 10, color: '#999' }}>{t('seconds')}</Text></Space>
        </Space>
      </div>
      {multiframes.map((mf, idx) => (
        <div key={mf.id} style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px', marginBottom: 6, border: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{t('frame_n', { n: idx + 1 })}</Tag>
            <Button size="small" type="text" danger icon={<DeleteOutlined />}
              onClick={() => setMultiframes(prev => prev.filter((_, i) => i !== idx))}
              disabled={multiframes.length <= 2}
              style={{ fontSize: 10, padding: '0 4px' }} />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0 }}>
              {mf.imageUrl ? (
                <div style={{ position: 'relative', width: 56, height: 56 }}>
                  <img src={mf.imageUrl.url || mf.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
                  <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
                    style={{ position: 'absolute', top: -6, right: -6, padding: 0 }}
                    onClick={() => setMultiframes(prev => prev.map((f, i) => i === idx ? { ...f, imageUrl: null } : f))} />
                </div>
              ) : (
                <Upload accept="image/*" showUploadList={false} key={`mf-up-${idx}`}
                  customRequest={({ file, onSuccess }) => {
                    multimodalAPI.uploadFile(file).then(res => {
                      const upUrl = res.url || res.URL;
                      if (upUrl) {
                        setMultiframes(prev => prev.map((f, i) => i === idx ? { ...f, imageUrl: { url: upUrl, name: file.name } } : f));
                        onSuccess && onSuccess(res, file);
                        message.success(t('upload_success'));
                      } else message.error(res.error || 'Upload failed');
                    }).catch(e => message.error(e.message || ''));
                  }}>
                  <div style={{ width: 56, height: 56, border: '1px dashed #d9d9d9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#999' }}>
                    <div style={{ textAlign: 'center' }}><PlusOutlined style={{ fontSize: 12 }} /><div style={{ fontSize: 9 }}>{t('upload_image_short')}</div></div>
                  </div>
                </Upload>
              )}
            </div>
            <Tooltip title={t('select_from_library')}>
              <Button size="small" type="text" icon={<PictureOutlined />}
                onClick={() => openImageLibrary('multiframe_' + idx)}
                style={{ fontSize: 10, padding: '0 2px', height: 20, flexShrink: 0 }} />
            </Tooltip>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input size="small" value={mf.transitionPrompt || ''}
                onChange={e => setMultiframes(prev => prev.map((f, i) => i === idx ? { ...f, transitionPrompt: e.target.value } : f))}
                placeholder={t('transition_desc_simple')} style={{ fontSize: 11 }} />
              <Space size={2} style={{ marginTop: 2 }}>
                <Input size="small" type="number" min={1} max={10} value={mf.transitionDuration || 2}
                  onChange={e => setMultiframes(prev => prev.map((f, i) => i === idx ? { ...f, transitionDuration: parseInt(e.target.value) || 2 } : f))}
                  style={{ width: 44, fontSize: 10 }} />
                <Text style={{ fontSize: 10, color: '#999' }}>{t('seconds')}</Text>
              </Space>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  };

  const renderVideoParams = () => {
    if (!currentFeat) return null;
    return (
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '14px 16px', marginTop: 12 }}>
        <Text strong style={{ fontSize: 13, marginBottom: 10, display: 'block' }}>
          <SettingOutlined /> {t('param_settings')}
        </Text>

        {/* {t('video_mode_label')}选择 */}
        {renderVideoModeSelector()}

        <Divider style={{ margin: '8px 0' }} />

        {/* 模式特定素材上传 */}
        {videoMode === 'full_reference' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                📷 {t('reference_image')} {currentFeat.maxRefImages > 0 && (
                  <Text type="secondary" style={{ fontSize: 10 }}>{t("reference_image_max", {count: currentFeat.maxRefImages})}</Text>
                )}
              </Text>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {refImages.map((img, i) => (
                  <div key={i} style={{ position: 'relative', width: 56, height: 56 }}>
                    {img.isAsset && <span style={{ position: 'absolute', top: -10, left: -6, background: '#1677ff', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, zIndex: 2, lineHeight: '15px', whiteSpace: 'nowrap' }}>图片{i + 1}</span>}
                    {img.isAsset ? (
                      img.displayUrl ? (
                        <img src={img.displayUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #e6f7ff, #f0f5ff)', borderRadius: 6, border: '1px solid #91caff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PictureOutlined style={{ fontSize: 14, color: '#1677ff' }} />
                          <span style={{ fontSize: 7, color: '#1677ff', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</span>
                          <span style={{ fontSize: 7, color: '#1677ff', background: '#bae0ff', padding: '0 2px', borderRadius: 2 }}>素材库</span>
                        </div>
                      )
                    ) : (
                      <img src={img.url || img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    )}
                    <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
                      style={{ position: 'absolute', top: -8, right: -8, padding: 0 }}
                      onClick={() => setRefImages(prev => prev.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                {(!currentFeat || (currentFeat.maxRefImages > 0 && refImages.length < currentFeat.maxRefImages)) && (
                  <Upload accept="image/*" showUploadList={false}
                    beforeUpload={(file) => {
                      if (file.size > 4 * 1024 * 1024) { message.error(t('file_too_large_4mb') || 'File too large, max 4MB'); return false; }
                      multimodalAPI.uploadFile(file).then(res => {
                        const upUrl = res.url || res.URL;
                        if (upUrl) { setRefImages(prev => [...prev, { url: upUrl, name: file.name }]); message.success(t('upload_success')); }
                        else { message.error(res.error || 'Upload failed'); }
                      }).catch(e => message.error(e.message || ''));
                      return false;
                    }}>
                    <div style={{
                      width: 56, height: 56, border: '1px dashed #d9d9d9',
                      borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: '#999',
                    }}>
                      <PlusOutlined style={{ fontSize: 14 }} />
                    </div>
                  </Upload>
                )}
              </div>
            </div>
            <Button size="small" type="link" icon={<PictureOutlined />}
              onClick={() => openImageLibrary('refImages')}
              style={{ fontSize: 11, padding: 0, height: 'auto', marginBottom: 12 }}>
              {t('select_from_library')}
            </Button>
            {currentFeat.maxRefVideos > 0 && renderMultiUpload(referenceVideos, setReferenceVideos, 'video/*', t('reference_video'), currentFeat.maxRefVideos, <VideoCameraOutlined />)}
            {currentFeat.maxRefVideos > 0 && (
              <Button size="small" type="link" icon={<VideoCameraOutlined />}
                onClick={() => openImageLibrary('referenceVideos')}
                style={{ fontSize: 11, padding: 0, height: 'auto', marginBottom: 12 }}>
                {t('select_from_library_video')}
              </Button>
            )}
            {currentFeat.maxRefAudio > 0 && renderMultiUpload(referenceAudio, setReferenceAudio, 'audio/*', t('reference_audio'), currentFeat.maxRefAudio, <SoundOutlined />)}
            {/* 有声视频开关 */}
            <div style={{ marginTop: 6 }}>
              <Space size={4}>
                <Switch size="small" checked={generateAudio} onChange={setGenerateAudio} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <AudioOutlined /> {t("generate_audio_toggle")}
                </Text>
              </Space>
            </div>
          </>
        )}

        {videoMode === 'start_end_frames' && (
          <>
            {renderImageUpload(firstFrameImage, setFirstFrameImage, '🎬 ' + t('upload_first_frame'))}
            <Button size="small" type="link" icon={<PictureOutlined />}
              onClick={() => openImageLibrary('firstFrame')}
              style={{ fontSize: 11, padding: 0, height: 'auto', marginBottom: 6 }}>
              {t('select_from_library')}
            </Button>
            {renderImageUpload(lastFrameImage, setLastFrameImage, '🏁 ' + t('upload_last_frame'))}
            <Button size="small" type="link" icon={<PictureOutlined />}
              onClick={() => openImageLibrary('lastFrame')}
              style={{ fontSize: 11, padding: 0, height: 'auto', marginBottom: 6 }}>
              {t('select_from_library')}
            </Button>
            <div style={{ marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Space size={4}>
                <Switch size="small" checked={generateAudio} onChange={setGenerateAudio} />
                <Text type="secondary" style={{ fontSize: 11 }}><AudioOutlined /> {t("generate_audio_toggle")}</Text>
              </Space>
              {lastFrameImage && (
                <Space size={4}>
                  <Switch size="small" checked={returnLastFrame} onChange={setReturnLastFrame} />
                  <Text type="secondary" style={{ fontSize: 11 }}>{t('return_last_frame_toggle')}</Text>
                </Space>
              )}
            </div>
          </>
        )}

        {videoMode === 'smart_multiframe' && (
          <>
            {renderMultiframeEditor()}
            <div style={{ marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Space size={4}>
                <Switch size="small" checked={generateAudio} onChange={setGenerateAudio} />
                <Text type="secondary" style={{ fontSize: 11 }}><AudioOutlined /> {t("generate_audio_toggle")}</Text>
              </Space>
            </div>
          </>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* 分辨率 */}
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>{t('resolution')}</Text>
          <Radio.Group
            value={resolution}
            onChange={e => setResolution(e.target.value)}
            size="small"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
            buttonStyle="solid"
          >
            {currentFeat.resolutions.map(r => (
              <Tooltip key={r.value} title={r.tip}>
                <Radio.Button value={r.value} disabled={r.disabled} style={{ flex: 1, minWidth: 60, textAlign: 'center', fontSize: 12 }}>
                  {r.label}
                </Radio.Button>
              </Tooltip>
            ))}
          </Radio.Group>
        </div>

        <Row gutter={16} style={{ marginBottom: 20 }}>
          {/* 时长 - 智能多帧自动计算，不显示滑块 */}
        {videoMode !== 'smart_multiframe' && (
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 2, display: 'block' }}>{t('duration_label')}: <Text strong>{duration}{t('seconds_unit')}</Text></Text>
            <Slider
              min={currentFeat.durationRange[0]}
              max={currentFeat.durationRange[1]}
              step={1}
              value={duration}
              onChange={setDuration}
              marks={{ [currentFeat.durationRange[0]]: `${currentFeat.durationRange[0]}s`, [currentFeat.durationRange[1]]: `${currentFeat.durationRange[1]}s` }}
              style={{ margin: '4px 0 0' }}
            />
          </Col>
        )}
        </Row>

        {/* 比例 */}
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>{t('aspect_ratio')}</Text>
          <Radio.Group
            value={aspectRatio}
            onChange={e => setAspectRatio(e.target.value)}
            size="small"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
            buttonStyle="solid"
          >
            {currentFeat.aspectRatios.map(r => (
              <Radio.Button key={r} value={r} style={{ flex: 1, minWidth: 50, textAlign: 'center', fontSize: 11, padding: '2px 4px' }}>{r}</Radio.Button>
            ))}
          </Radio.Group>
        </div>

        {/* Seed + 水印 */}
        <Row gutter={16} align="middle" style={{ flexWrap: 'wrap', gap: 4 }}>
          <Col>
            <Space size={4} align="center">
              <Switch size="small" checked={!useRandomSeed} onChange={v => setUseRandomSeed(!v)} />
              <Text type="secondary" style={{ fontSize: 12 }}>{t('custom_seed')}</Text>
              {!useRandomSeed && (
                <Input
                  size="small"
                  style={{ width: 90 }}
                  placeholder={t('custom_seed')}
                  type="number"
                  value={videoSeed ?? ''}
                  onChange={e => setVideoSeed(e.target.value ? parseInt(e.target.value) : null)}
                />
              )}
            </Space>
          </Col>
          <Col>
            <Space size={4}>
              <Switch size="small" checked={watermark} onChange={setWatermark} />
              <Text type="secondary" style={{ fontSize: 12 }}>{t('watermark_label')}</Text>
            </Space>
          </Col>
        </Row>

        {/* 高级选项 */}
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>{t('advanced_options')}</Text>

          {/* 负面提示词 */}
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Negative Prompt</Text>
            <Input.TextArea
              size="small"
              rows={2}
              placeholder={t('negative_prompt_hint') || 'Describe what you don\'t want...'}
              value={videoNegativePrompt}
              onChange={e => setVideoNegativePrompt(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>

          <Space size={12} wrap>
            <Space size={4}>
              <Switch size="small" checked={cameraFixed} onChange={setCameraFixed} />
              <Text type="secondary" style={{ fontSize: 11 }}>{t('camera_fixed') || 'Fixed Camera'}</Text>
            </Space>
          </Space>
        </div>
      </div>
    );
  };

  const renderImageParams = () => {
    if (!currentFeat) return null;
    return (
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '14px 16px', marginTop: 12 }}>
        <Text strong style={{ fontSize: 13, marginBottom: 10, display: 'block' }}>
          <SettingOutlined /> {t('param_settings')}
        </Text>

        {/* 尺寸 */}
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>{t('output_size')}</Text>
          <Radio.Group
            value={imageSize}
            onChange={e => setImageSize(e.target.value)}
            size="small"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
            buttonStyle="solid"
          >
            {currentFeat.sizes.map(s => (
              <Radio.Button key={s.value} value={s.value} disabled={s.disabled} style={{ fontSize: 11, padding: '2px 8px' }}>
                {s.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>

        {/* 数量 + 引导强度 */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 2, display: 'block' }}>{t('generate_count')}: <Text strong>1</Text> (官方单次仅支持1张)</Text>
          </Col>
{!currentFeat?.async && (
          <Col span={12}>
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 2, display: 'block' }}>{t('guidance_scale')}: <Text strong>{guidanceScale}</Text></Text>
            <Slider min={1} max={20} step={0.5} value={guidanceScale} onChange={setGuidanceScale}
              marks={{ 1: '1', 20: '20' }} style={{ margin: '4px 0 0' }} />
          </Col>
)}
        </Row>

        {/* 质量选择 — 仅异步API显示 */}
        {currentFeat?.async && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>Quality</Text>
          <Radio.Group value={imageQuality} onChange={e => {
            setImageQuality(e.target.value);
            // 根据质量更新计费提示
          }} size="small" buttonStyle="solid">
            <Radio.Button value="low" style={{ fontSize: 11 }}>Low</Radio.Button>
            <Radio.Button value="medium" style={{ fontSize: 11 }}>Medium</Radio.Button>
            <Radio.Button value="high" style={{ fontSize: 11 }}>High</Radio.Button>
            <Radio.Button value="auto" style={{ fontSize: 11 }}>Auto</Radio.Button>
          </Radio.Group>
          {currentFeat.quality_pricing && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              ${currentFeat.quality_pricing[imageQuality] || currentFeat.quality_pricing.medium}/image
            </Text>
          )}
        </div>
        )}

{/* 风格 — 异步API跳过 */}
{!currentFeat?.async && (
        <div style={{ marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>{t('art_style')}</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {currentFeat.styles.map(s => (
              <Tag.CheckableTag
                key={s.value}
                checked={imageStyle === s.value}
                onChange={() => setImageStyle(s.value)}
                style={{ fontSize: 11, padding: '2px 8px', margin: 0 }}
              >
                {s.emoji} {s.label}
              </Tag.CheckableTag>
            ))}
          </div>
        </div>
)}

{/* Seed — 异步API跳过 */}
{!currentFeat?.async && (
        <Space size={4} align="center">
          <Switch size="small" checked={!useRandomImageSeed} onChange={v => setUseRandomImageSeed(!v)} />
          <Text type="secondary" style={{ fontSize: 12 }}>{t('custom_seed')}</Text>
          {!useRandomImageSeed && (
            <Input size="small" style={{ width: 90 }} placeholder={t('custom_seed')} type="number"
              value={imageSeed ?? ''} onChange={e => setImageSeed(e.target.value ? parseInt(e.target.value) : null)} />
          )}
        </Space>
)}
      </div>
    );
  };

  // 预览弹窗详情面板内容
  const renderPreviewDetail = (record) => {
    if (!record) return null;
    const isVideo = record.type === 'video';
    return (
      <div style={{ fontSize: 12 }}>
        {/* 生成时间 */}
        <Row style={{ marginBottom: 8 }}>
          <Col span={8}><Text type="secondary">{t('generation_time')}</Text></Col>
          <Col span={16}><Text>{formatTimeDisplay(record.createdAt)}</Text></Col>
        </Row>
        {/* 生成耗时 */}
        <Row style={{ marginBottom: 8 }}>
          <Col span={8}><Text type="secondary">{t('generation_duration')}</Text></Col>
          <Col span={16}><Text>{record.duration > 0 ? `${record.duration}${t('seconds_unit')}` : '-'}</Text></Col>
        </Row>
        {/* 参数列表 */}
        <Row style={{ marginBottom: 8 }}>
          <Col span={8}><Text type="secondary">{t('param_settings')}</Text></Col>
          <Col span={16}>
            <Space size={[2, 4]} wrap>
              <Tag style={{ margin: 0, fontSize: 11 }}>{t('model')}: {record.model}</Tag>
              {isVideo ? (
                <>
                  {record.params?.resolution && <Tag style={{ margin: 0, fontSize: 11 }}>{t('resolution')}: {record.params.resolution}</Tag>}
                  {record.params?.duration && <Tag style={{ margin: 0, fontSize: 11 }}>{t('duration_label')}: {record.params.duration}s</Tag>}
                  {record.params?.aspectRatio && <Tag style={{ margin: 0, fontSize: 11 }}>{t('aspect_ratio')}: {record.params.aspectRatio}</Tag>}
                  {record.params?.watermark !== undefined && <Tag style={{ margin: 0, fontSize: 11 }} color={record.params.watermark ? 'red' : 'default'}>{t('watermark_label')}: {record.params.watermark ? t('yes') : t('no')}</Tag>}
                </>
              ) : (
                <>
                  {record.params?.size && <Tag style={{ margin: 0, fontSize: 11 }}>{t('output_size')}: {record.params.size}</Tag>}
                  {record.params?.count > 1 && <Tag style={{ margin: 0, fontSize: 11 }}>{t('generate_count')}: {record.params.count}</Tag>}
                  {record.params?.quality && <Tag style={{ margin: 0, fontSize: 11 }} color="blue">Quality: {record.params.quality}</Tag>}
                  {record.params?.style && <Tag style={{ margin: 0, fontSize: 11 }}>{t('art_style')}: {record.params.style}</Tag>}
                  {record.params?.guidanceScale != null && <Tag style={{ margin: 0, fontSize: 11 }}>{t('guidance_scale')}: {record.params.guidanceScale}</Tag>}
                </>
              )}
              {record.params?.seed != null && <Tag style={{ margin: 0, fontSize: 11 }}>{t('custom_seed')}: {record.params.seed}</Tag>}
            </Space>
          </Col>
        </Row>
        {/* 参考素材（全参考 + 首尾帧 + 智能多帧） */}
          {(record.refImages?.length > 0 || record.refVideos?.length > 0 || record.refAudio?.length > 0 ||
            record.firstFrameImage || record.lastFrameImage || record.keyframes?.length > 0) && (
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text type="secondary">{t('material_files')}</Text></Col>
            <Col span={16}>
              <Space size={4} wrap>
                {record.refImages?.map((img, i) => (
                    (typeof img === 'string' && img.startsWith('asset://')) ? (
                      <Tag key={`img-${i}`} color="blue" style={{ fontSize: 10, margin: 0 }}>{img.replace('asset://', '')}</Tag>
                    ) : (
                      <Image key={`img-${i}`} src={typeof img === 'object' ? (img.displayUrl || img.url) : img} width={40} height={40} style={{ borderRadius: 4, objectFit: 'cover' }} preview={{ mask: <EyeOutlined /> }} />
                    )
                  ))}
                {record.refVideos?.map((v, i) => {
                    const vUrl = typeof v === 'object' ? (v.displayUrl || v.url) : v;
                    return (
                      <div key={`vid-${i}`} style={{ width: 40, height: 40, borderRadius: 4, background: '#000', position: 'relative', overflow: 'hidden', border: '1px solid #333' }}>
                        <video src={vUrl} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />
                        <PlayCircleOutlined style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 14, color: '#fff' }} />
                      </div>
                    );
                  })}
                {record.refAudio?.map((a, i) => {
                    const aUrl = typeof a === 'object' ? (a.displayUrl || a.url) : a;
                    return <Tag key={`aud-${i}`} color="purple" style={{ fontSize: 10, margin: 0 }}><SoundOutlined style={{ marginRight: 3 }} />{t('audio')} {i+1}</Tag>;
                  })}
                {record.firstFrameImage && (
                  <div style={{ position: 'relative' }}>
                    <Image src={typeof record.firstFrameImage === 'object' ? (record.firstFrameImage.displayUrl || record.firstFrameImage.url) : record.firstFrameImage}
                      width={40} height={40} style={{ borderRadius: 4, objectFit: 'cover' }} preview={{ mask: <EyeOutlined /> }} />
                    <Tag color="green" style={{ fontSize: 9, position: 'absolute', bottom: -6, left: 2, margin: 0, padding: '0 4px', lineHeight: '14px' }}>首帧</Tag>
                  </div>
                )}
                {record.lastFrameImage && (
                  <div style={{ position: 'relative' }}>
                    <Image src={typeof record.lastFrameImage === 'object' ? (record.lastFrameImage.displayUrl || record.lastFrameImage.url) : record.lastFrameImage}
                      width={40} height={40} style={{ borderRadius: 4, objectFit: 'cover' }} preview={{ mask: <EyeOutlined /> }} />
                    <Tag color="orange" style={{ fontSize: 9, position: 'absolute', bottom: -6, left: 2, margin: 0, padding: '0 4px', lineHeight: '14px' }}>尾帧</Tag>
                  </div>
                )}
                {record.keyframes?.map((kf, i) => (
                  <div key={`kf-${i}`} style={{ position: 'relative' }}>
                    <Image src={typeof kf.imageUrl === 'object' ? (kf.imageUrl.displayUrl || kf.imageUrl.url) : kf.imageUrl}
                      width={40} height={40} style={{ borderRadius: 4, objectFit: 'cover' }} preview={{ mask: <EyeOutlined /> }} />
                    <Tag color="cyan" style={{ fontSize: 9, position: 'absolute', bottom: -6, left: 2, margin: 0, padding: '0 4px', lineHeight: '14px' }}>帧{i+1}</Tag>
                  </div>
                ))}
              </Space>
            </Col>
          </Row>
          )}
          {!record.refImages?.length && !record.refVideos?.length && !record.refAudio?.length &&
            !record.firstFrameImage && !record.lastFrameImage && !record.keyframes?.length && (
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text type="secondary">{t('material_files')}</Text></Col>
            <Col span={16}><Text type="secondary">{t('no_ref_images')}</Text></Col>
          </Row>
          )}
{/* 任务ID（仅视频） */}
        {isVideo && record.taskId && (
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text type="secondary">{t('task_query_id')}</Text></Col>
            <Col span={16}>
              <Space size={4}>
                <Text code copyable={{ text: record.taskId, tooltips: [t('copy_task_id'), t('copied')] }} style={{ fontSize: 11, maxWidth: 280 }}>
                  {record.taskId.length > 36 ? record.taskId.substring(0, 36) + '...' : record.taskId}
                </Text>
              </Space>
            </Col>
          </Row>
        )}
        {/* 生成ID（图片） */}
        {!isVideo && record.requestId && (
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text type="secondary">{t('task_query_id')}</Text></Col>
            <Col span={16}>
              <Space size={4}>
                <Text code copyable={{ text: record.requestId, tooltips: [t('copy_task_id'), t('copied')] }} style={{ fontSize: 11, maxWidth: 280 }}>
                  {record.requestId.length > 36 ? record.requestId.substring(0, 36) + '...' : record.requestId}
                </Text>
              </Space>
            </Col>
          </Row>
        )}
        {/* 提示词 */}
        <Row style={{ marginBottom: 4 }}>
          <Col span={8}><Text type="secondary">{t('prompt_description')}</Text></Col>
          <Col span={16}>
            <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{record.prompt}</Text>
          </Col>
        </Row>
        {record.negativePrompt && (
          <Row>
            <Col span={8}><Text type="secondary">{t('negative_prompt_label')}</Text></Col>
            <Col span={16}>
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{record.negativePrompt}</Text>
            </Col>
          </Row>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <style>{`
        @keyframes thumbSlideIn {
          0% { transform: translateX(-100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .popover-image-item .popover-download-btn { opacity: 0; transition: opacity 0.15s; }
        .popover-image-item:hover .popover-download-btn { opacity: 1; }
        .preview-image-wrapper .preview-download-btn { opacity: 0; transition: opacity 0.2s; }
        .preview-image-wrapper:hover .preview-download-btn { opacity: 1; }
      `}</style>
      {/* ======== 顶部标题栏 ======== */}
      <div style={{
        padding: '16px 24px', background: '#fff', borderBottom: '1px solid #f0f0f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <Space size={12}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 50%, #f0932b 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BulbOutlined style={{ fontSize: 20, color: '#fff' }} />
          </div>
          <div>
            <Space size={4}>
              <Title level={4} style={{ margin: 0 }}>{t('creative_inspiration')}</Title>
              <Tag color="#ff4d4f" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', borderRadius: 4 }}>
                <FireOutlined /> {t('creative_hot')}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{t('creative_inspiration_desc')}</Text>
          </div>
        </Space>
        <Segmented
          size="small"
          value={activeTab}
          onChange={v => { setActiveTab(v); localStorage.setItem('creativeTab', v); }}
          options={[
            { label: <Space size={4}><VideoCameraOutlined />{t('video_creation')}</Space>, value: 'video' },
            { label: <Space size={4}><PictureOutlined />{t('image_creation')}</Space>, value: 'image' },
          ]}
          style={{ background: '#f5f5f5' }}
        />
      </div>

      {/* ======== 主体三栏布局 ======== */}
      <div style={{
        flex: 1, display: 'flex', overflow: 'hidden',
        padding: '16px 24px', gap: 16,
      }}>
        {/* ===== 左栏：模型选择 ===== */}
        <div style={{ width: 280, flexShrink: 0, overflow: 'auto' }}>
          <Card
            size="small"
            title={<Space size={4}><ExperimentOutlined /><Text strong style={{ fontSize: 13 }}>{t('select_model_version')}</Text></Space>}
            styles={{ body: { padding: '8px 12px', maxHeight: '100%', overflow: 'auto' } }}
          >
            {currentModels.length === 0 ? (
              <Empty description={t('no_models_available')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              currentModels.map(m => renderModelCard(m, activeTab))
            )}
          </Card>
        </div>

        {/* ===== 中栏：创作区 ===== */}
        <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          <Card
            size="small"
            styles={{ body: { padding: 16 } }}
          >
            {/* 当前模型信息 */}
            {currentFeat && (
              <div style={{
                background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
              }}>
                <Space size={6}>
                  <Tag color={currentFeat.tierColor} style={{ margin: 0 }}>{currentFeat.tier}</Tag>
                  <Text strong style={{ fontSize: 13 }}>{selectedModel}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{currentFeat.desc}</Text>
                </Space>
                <Space size={2} wrap>
                  {(currentFeat.features || []).map((f, i) => (
                    <Tag key={i} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>{f}</Tag>
                  ))}
                </Space>
              </div>
            )}

            {/* 提示词 - 智能多帧自动生成 */}
            {videoMode === 'smart_multiframe' ? (
              <div style={{ marginBottom: 20, padding: '8px 12px', background: '#f6f8fa', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('prompt_auto_generated')}</Text>
              </div>
            ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Text strong style={{ fontSize: 13 }}>{t('prompt_description')} *</Text>
                <Button size="small" type="text" icon={<ClearOutlined />} onClick={clearPrompt} disabled={!prompt && !negativePrompt}
                  style={{ fontSize: 11, color: '#999' }}>{t('clear_prompt')}</Button>
              </div>
              <TextArea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={3}
                style={{ fontSize: 13 }}
                disabled={activeTab === 'video' && videoMode === 'smart_multiframe'}
                placeholder={activeTab === 'video'
                  ? t('creative_prompt_placeholder_video')
                  : t('creative_prompt_placeholder_image')}
                maxLength={1000}
                showCount
              />
            </div>
            )}

            {/* {t('ai_prompt_assistant')}按钮 */}
            <div style={{ marginBottom: 12 }}>
              <Button
                icon={<RobotOutlined />}
                onClick={() => {
                  setAiModalOpen(true);
                  setAiModalIntent('');
                  setAiGeneratedGroups([]);
                }}
                style={{ fontSize: 12 }}
              >
                {t('ai_prompt_assistant')}
              </Button>
              {(refImages.some(img => img.isAsset) || referenceVideos.some(v => v.isAsset)) && (
                <div>
                  <BulbOutlined style={{ color: '#faad14', fontSize: 12, marginRight: 6 }} />
                  <Text style={{ fontSize: 11, color: '#ad6800' }}>
                    {t('prompt_reference_hint_start')}<Text code style={{ fontSize: 10, background: '#fff1b8' }}>图片N</Text> <Text code style={{ fontSize: 10, background: '#fff1b8' }}>视频N</Text>{t('prompt_reference_hint_end')}
                  </Text>
                </div>
              )}
            </div>

            {/* 负面提示词 - 仅图片模式显示，异步API不支持 */}
            {activeTab === 'image' && !currentFeat?.async && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{t('negative_prompt_label')} <Tooltip title={t('negative_prompt_tip')}><InfoCircleOutlined /></Tooltip></Text>
              </div>
              <TextArea
                value={negativePrompt}
                onChange={e => setNegativePrompt(e.target.value)}
                rows={1}
                style={{ fontSize: 12 }}
                placeholder={t('negative_prompt_placeholder')}
                maxLength={500}
              />
            </div>
            )}

            {/* 参考图上传 - 仅图片模式显示（视频模式的参考素材在参数设置区） */}
            {activeTab === 'image' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  {t('reference_image')}
                  {currentFeat?.maxRefImages > 0 && (
                    <Text type="secondary" style={{ fontSize: 11 }}> {t('reference_image_max', { count: currentFeat.maxRefImages })}</Text>
                  )}
                </Text>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, marginBottom: 10 }}>
                  {refImages.map((img, i) => (
                    <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                      {img.isAsset && <span style={{ position: 'absolute', top: -10, left: -6, background: '#1677ff', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, zIndex: 2, lineHeight: '15px', whiteSpace: 'nowrap' }}>图片{i + 1}</span>}
                      {img.isAsset ? (
                        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #e6f7ff, #f0f5ff)', borderRadius: 6, border: '1px solid #91caff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PictureOutlined style={{ fontSize: 16, color: '#1677ff' }} />
                          <span style={{ fontSize: 7, color: '#1677ff', maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</span>
                          <span style={{ fontSize: 7, color: '#1677ff', background: '#bae0ff', padding: '0 2px', borderRadius: 2 }}>素材库</span>
                        </div>
                      ) : (
                        <img src={img.url || img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      )}
                      <Button
                        size="small" danger type="text" icon={<CloseCircleOutlined />}
                        style={{ position: 'absolute', top: -8, right: -8, padding: 0 }}
                        onClick={() => setRefImages(prev => prev.filter((_, idx) => idx !== i))}
                      />
                    </div>
                  ))}
                  {(!currentFeat || refImages.length < (currentFeat.maxRefImages || 10)) && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        if (file.size > 4 * 1024 * 1024) { message.error(t('file_too_large_4mb') || 'File too large, max 4MB'); return false; }
                        multimodalAPI.uploadFile(file).then(res => {
                          const upUrl = res.url || res.URL;
                          if (upUrl) { setRefImages(prev => [...prev, { url: upUrl, name: file.name }]); message.success(t('upload_success')); }
                          else { message.error(res.error || 'Upload failed'); }
                        }).catch(e => message.error(e.message || ''));
                        return false;
                      }}
                    >
                      <div style={{
                        width: 64, height: 64, border: '1px dashed #d9d9d9',
                        borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#999', fontSize: 11,
                      }}>
                        <Space direction="vertical" size={0} align="center">
                          <PlusOutlined />
                          <span>{t('reference_image_btn')}</span>
                        </Space>
                      </div>
                    </Upload>
                  )}
                </div>
                <Button size="small" type="link" icon={<PictureOutlined />}
                  onClick={() => openImageLibrary('refImages')}
                  style={{ fontSize: 11, padding: 0, height: 'auto', marginTop: 4 }}>
                  {t('select_from_library')}
                </Button>
              </div>
            </div>
            )}

            {/* 参数区域 */}
            {activeTab === 'video' ? renderVideoParams() : renderImageParams()}

            {/* 生成按钮 */}
            <Button
              type="primary"
              size="large"
              block
              icon={generating ? <LoadingOutlined /> : <ThunderboltOutlined />}
              loading={generating}
              onClick={handleGenerate}
              disabled={(!prompt.trim() && videoMode !== 'smart_multiframe') || !selectedModel || generating}
              style={{ marginTop: 12, height: 44, fontSize: 15, fontWeight: 600, borderRadius: 8 }}
            >
              {generating
                ? (activeTab === 'video' ? t('video_generating_text') : t('image_generating_text'))
                : (activeTab === 'video' ? t('generate_video_btn') : t('generate_image_btn'))
              }
            </Button>
          </Card>
        </div>

        {/* ===== 右栏：历史记录 ===== */}
        <div style={{ width: 360, flexShrink: 0, overflow: 'auto' }}>
          <Card
            size="small"
            title={
              <Space size={4}>
                <HistoryOutlined />
                <Text strong style={{ fontSize: 13 }}>{t('generation_history')}</Text>
                <Badge count={historyFiltered.length} size="small" style={{ backgroundColor: '#1677ff' }} />
              </Space>
            }
            extra={
              historyFiltered.length > 0 && (
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={clearHistory} style={{ fontSize: 11 }}>
                  {t('clear')}
                </Button>
              )
            }
            styles={{ body: { padding: '4px 8px', maxHeight: '100%', overflow: 'auto' } }}
          >
            {historyFiltered.length === 0 ? (
              <Empty description={t('no_history')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '20px 0' }} />
            ) : (
              historyFiltered.map(item => {
                const imageCount = item.type === 'image' ? (item.imageUrls?.length || 0) : 0;
                const isVideo = item.type === 'video';
                const hasPreview = isVideo ? !!item.videoUrl : (item.imageUrls?.length > 0);
                // 多图时取幻灯片索引，单图或视频用第一帧
                const thumbImgSrc = isVideo ? null : (imageCount > 1 ? (item.imageUrls[thumbSlides[item.id] || 0] || item.imageUrls[0]) : item.imageUrls[0]);
                // 多张图片：构建下载弹出内容
                const multiImagePopover = imageCount > 1 && isDownloadableUrl(item.imageUrls[0]) ? (
                  <div style={{ maxWidth: 280 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#333' }}>
                      {t('download_image')} ({imageCount} {t('image_label')})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {item.imageUrls.map((url, i) => (
                        <div
                          key={i}
                          className="popover-image-item"
                          style={{ cursor: 'pointer', position: 'relative', borderRadius: 4, overflow: 'hidden' }}
                          title={t('download_image') + ' ' + (i + 1)}
                        >
                          <img src={url} alt={'img ' + (i + 1)} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', display: 'block' }} />
                          <Tag color="blue" style={{ position: 'absolute', top: 2, left: 2, fontSize: 9, lineHeight: '14px', padding: '0 3px', margin: 0, borderRadius: 2 }}>
                            {i + 1}
                          </Tag>
                          <div
                            className="popover-download-btn"
                            onClick={(e) => { e.stopPropagation(); downloadSingleFile(url, 'image_' + item.id + '_' + (i + 1) + '.' + getFileExt(url)); }}
                            style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 22, height: 22,
                              background: 'rgba(0,0,0,0.5)',
                              borderRadius: '4px 0 4px 0',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <DownloadOutlined style={{ fontSize: 11, color: '#fff' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button
                      block size="small" type="primary" ghost
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadAllAsZip(item.imageUrls, 'images_' + item.id);
                      }}
                    >
                      {t('download_all_zip')}
                    </Button>
                  </div>
                ) : null;
                const handlePreview = (e) => {
                  e.stopPropagation();
                  if (isVideo && item.videoUrl) {
                    setPreviewData({ type: 'video', url: item.videoUrl, prompt: item.prompt, record: item });
                    setPreviewVisible(true);
                  } else if (!isVideo && item.imageUrls?.length > 0) {
                    setPreviewImageIndex(0);
                    setPreviewData({ type: 'image', urls: item.imageUrls, prompt: item.prompt, record: item });
                    setPreviewVisible(true);
                  }
                };
                return (
                <div
                  key={item.id}
                  style={{
                    marginBottom: 6, borderRadius: 6, background: '#fff',
                    border: '1px solid #f0f0f0',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#d9d9d9'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#f0f0f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', gap: 10, padding: 8, alignItems: 'stretch' }}>
                    {/* 左：预览图 */}
                    <div
                      onClick={handlePreview}
                      title={hasPreview ? t('preview') : undefined}
                      style={{
                      width: 68, height: 68, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
                      background: isVideo ? '#000' : '#f5f5f5', position: 'relative',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '1px solid #f0f0f0',
                      cursor: hasPreview ? 'pointer' : 'default',
                    }}>
                      {isVideo ? (
                        item.videoUrl ? (
                          <>
                            <video src={item.videoUrl} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <div style={{
                              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(0,0,0,0.25)',
                            }}>
                              <PlayCircleOutlined style={{ fontSize: 22, color: 'rgba(255,255,255,0.9)' }} />
                            </div>
                          </>
                        ) : (
                          <PlayCircleOutlined style={{ fontSize: 26, color: '#fff', opacity: 0.85 }} />
                        )
                      ) : item.imageUrls?.length > 0 ? (
                        <>
                          {item.expired || imgErrors.has(item.id) ? (
                            <div style={{
                              width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: '#f5f5f5', flexDirection: 'column', gap: 2,
                            }}>
                              <PictureOutlined style={{ fontSize: 16, color: '#bbb' }} />
                              <span style={{ fontSize: 9, color: '#999' }}>{t("image_expired")}</span>
                            </div>
                          ) : (
                            <img
                              key={item.id + '-' + (thumbSlides[item.id] || 0)}
                              src={thumbImgSrc} alt=""
                              style={{
                                width: '100%', height: '100%', objectFit: 'cover',
                                animation: 'thumbSlideIn 0.35s ease-out',
                              }}
                              onError={async (e) => {
                          // 尝试刷新过期的签名URL
                          if (!imgErrors.has(item.id)) {
                            try {
                              const refreshed = await multimodalAPI.getImageRecordURL(item.id);
                              if (refreshed.image_url && refreshed.image_url !== e.target.src) {
                                e.target.src = refreshed.image_url;
                                return; // 刷新成功，不标记错误
                              }
                            } catch (_) {}
                          }
                          setImgErrors(prev => { const next = new Set(prev); next.add(item.id); return next; });
                        }}
                            />
                          )}
                          <div style={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(0,0,0,0.15)', opacity: 0, transition: 'opacity 0.2s',
                          }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '0'; }}
                          >
                            <EyeOutlined style={{ fontSize: 20, color: 'rgba(255,255,255,0.9)' }} />
                          </div>
                          {/* 多图时显示左右切换箭头 */}
                          {imageCount > 1 && (
                            <>
                              <div
                                onClick={(e) => handleThumbSlide(e, item.id, 'prev', imageCount)}
                                style={{
                                  position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', zIndex: 3, opacity: 0.7,
                                  transition: 'opacity 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                              >
                                <LeftOutlined style={{ fontSize: 12, color: '#fff', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }} />
                              </div>
                              <div
                                onClick={(e) => handleThumbSlide(e, item.id, 'next', imageCount)}
                                style={{
                                  position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', zIndex: 3, opacity: 0.7,
                                  transition: 'opacity 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                              >
                                <RightOutlined style={{ fontSize: 12, color: '#fff', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }} />
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <PictureOutlined style={{ fontSize: 24, color: '#ccc' }} />
                      )}
                      {imageCount > 1 && (
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'rgba(0,0,0,0.45)',
                          fontSize: 10, lineHeight: '18px',
                          color: '#fff', textAlign: 'center',
                          borderRadius: '0 0 6px 6px',
                        }}>
                          {(thumbSlides[item.id] || 0) + 1}/{imageCount}
                        </div>
                      )}
                    </div>

                    {/* 右：信息 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      {/* 第一行：类型 + 模型 + 状态 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space size={4}>
                          <Tag color={isVideo ? 'blue' : 'purple'} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                            {isVideo ? t('video_label') : t('image_label')}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 10 }}>{item.model}</Text>
                        </Space>
                        {renderStatus(item.status)}
                      </div>

                      {/* 提示词（最多2行） */}
                      <Paragraph
                        ellipsis={{ rows: 2, tooltip: item.prompt }}
                        style={{ fontSize: 11, color: '#333', lineHeight: 1.4, margin: '3px 0', marginBottom: 0 }}
                      >
                        {item.prompt}
                      </Paragraph>

                      {/* 底行：时间 + 耗时 + 操作按钮 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Space size={4} style={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
                          {item.createdAt && (
                            <Text type="secondary" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                              <ClockCircleOutlined style={{ marginRight: 1 }} />{formatTimeShort(item.createdAt)}
                            </Text>
                          )}
                          {item.duration > 0 && (
                            <Text type="secondary" style={{ fontSize: 10, color: '#8c8c8c', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.duration}s</Text>
                          )}
                        </Space>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                          {item.type === 'video' && item.taskId && (
                            <Tooltip title={t('manual_check')}>
                              <Button size="small" type="text" icon={<ReloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={(e) => { e.stopPropagation(); manualCheckVideo(item); }} />
                            </Tooltip>
                          )}
                          {item.type === 'image' && item.requestId && (
                            <Tooltip title={t('manual_check')}>
                              <Button size="small" type="text" icon={<ReloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={(e) => { e.stopPropagation(); manualCheckImage(item); }} />
                            </Tooltip>
                          )}
                          {hasPreview && (
                            <Tooltip title={t('preview')}>
                              <Button size="small" type="text" icon={<EyeOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#52c41a' }}
                                onClick={handlePreview} />
                            </Tooltip>
                          )}
                          {/* 下载按钮 - 视频 */}
                          {isVideo && hasPreview && isDownloadableUrl(item.videoUrl) && (
                            <Tooltip title={t('download_video')}>
                              <Button size="small" type="text" icon={<DownloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={(e) => { e.stopPropagation(); downloadSingleFile(item.videoUrl, 'video_' + item.id + '.mp4'); }} />
                            </Tooltip>
                          )}
                          {/* 下载按钮 - 单张图片 */}
                          {!isVideo && hasPreview && imageCount === 1 && isDownloadableUrl(item.imageUrls[0]) && (
                            <Tooltip title={t('download_image')}>
                              <Button size="small" type="text" icon={<DownloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={(e) => { e.stopPropagation(); downloadSingleFile(item.imageUrls[0], 'image_' + item.id + '.' + getFileExt(item.imageUrls[0])); }} />
                            </Tooltip>
                          )}
                          {/* 下载按钮 - 多张图片（弹出选择面板） */}
                          {!isVideo && hasPreview && imageCount > 1 && multiImagePopover && (
                            <Popover content={multiImagePopover} trigger="click" placement="bottomRight" title={null}>
                              <Tooltip title={t('download_image')}>
                                <Button size="small" type="text" icon={<DownloadOutlined />}
                                  style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                  onClick={(e) => e.stopPropagation()} />
                              </Tooltip>
                            </Popover>
                          )}
                          {(item.status === 'failed' || item.status === 'error' || item.status === 'cancelled' || item.status === 'canceled') && item.errorMsg && (
                            <Tooltip title={item.errorMsg}>
                              <ExclamationCircleOutlined
                                style={{ fontSize: 14, color: '#ff4d4f', cursor: 'help', padding: '0 2px' }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Tooltip>
                          )}
                          {(item.status === 'completed' || item.status === 'succeeded' || item.status === 'active') && hasPreview && (
                            <Tooltip title={t("convert_to_aigc_group")}>
                              <Button size="small" type="text" icon={<ThunderboltOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#fa8c16' }}
                                onClick={(e) => { e.stopPropagation(); openConvertModal(item); }} />
                            </Tooltip>
                          )}
                          <Tooltip title={t('delete')}>
                            <Button size="small" type="text" icon={<DeleteOutlined />}
                              style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20 }}
                              onClick={(e) => { e.stopPropagation(); deleteHistoryItem(item.id); }} />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )})
            )}
          </Card>
        </div>
      </div>

      {/* ======== {t('ai_prompt_assistant')}弹窗 ======== */}
      <Modal
        title={<Space size={8}><RobotOutlined style={{ color: '#1677ff' }} />{t('ai_prompt_assistant')}</Space>}
        open={aiModalOpen}
        onCancel={() => { setAiModalOpen(false); setAiModalIntent(''); setAiGeneratedGroups([]); }}
        footer={null}
        width={780}
        destroyOnHidden
      >
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            {t('ai_prompt_modal_intro')}
          </Text>
          <TextArea
            value={aiModalIntent}
            onChange={e => setAiModalIntent(e.target.value)}
            rows={3}
            style={{ fontSize: 13 }}
            placeholder={activeTab === 'video'
              ? t('ai_prompt_modal_placeholder')
              : '例如：一只猫咪在樱花树下睡觉，柔和阳光，吉卜力风格...'}
            maxLength={500}
            showCount
            disabled={aiGenerating}
          />
        </div>

        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t("model")}:</Text>
            <Select
              value={aiModel}
              onChange={setAiModel}
              size="small"
              style={{ width: 160 }}
              options={textModels.length > 0
                ? textModels.map(m => ({
                    value: m.name || m.model_name,
                    label: m.display_name || m.model_name || m.name,
                  }))
                : [
                    { value: 'deepseek-chat', label: 'DeepSeek Chat' },
                    { value: 'deepseek-reasoner', label: 'DeepSeek R1' },
                    { value: 'gpt-4o', label: 'GPT-4o' },
                    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
                  ]
              }
            />
          </Space>
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t("group_count")}:</Text>
            <Radio.Group
              value={aiGroupCount}
              onChange={e => setAiGroupCount(e.target.value)}
              size="small"
              optionType="button"
              buttonStyle="solid"
              disabled={aiGenerating}
            >
              <Radio.Button value={1}>1 {t("group")}</Radio.Button>
              <Radio.Button value={2}>2 {t("group")}</Radio.Button>
              <Radio.Button value={3}>3 {t("group")}</Radio.Button>
            </Radio.Group>
          </Space>
          <Button
            type="primary"
            icon={aiGenerating ? <LoadingOutlined /> : <RobotOutlined />}
            loading={aiGenerating}
            onClick={generateAIPrompt}
            disabled={!aiModalIntent.trim() || aiGenerating}
            style={{ fontSize: 13 }}
          >
            {aiGenerating ? t('ai_prompt_generating_status') : t('ai_prompt_gen_btn')}
          </Button>
        </div>

        {/* 生成结果 - 多组 */}
        {(aiGeneratedGroups.length > 0 || aiGenerating) && (
          <div style={{ maxHeight: '55vh', overflow: 'auto', paddingRight: 4 }}>
            {aiGenerating && aiGeneratedGroups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>{t("ai_prompt_analyzing")}</Text>
              </div>
            ) : (
              aiGeneratedGroups.map((group, idx) => (
                <div key={idx} style={{
                  background: '#f6ffed', borderRadius: 8, padding: '10px 12px',
                  border: '1px solid #b7eb8f', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Text strong style={{ fontSize: 13, color: '#1677ff' }}>
                      <StarFilled style={{ fontSize: 11, marginRight: 4 }} />
                      {group.title || `方案 ${idx + 1}`}
                    </Text>
                    <Button
                      type="primary" size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={() => {
                        setPrompt(group.prompt);
                        // Seedance（视频）不支持负面词，仅 Seedream（图片）可设置负面词
                        setNegativePrompt(activeTab === 'image' ? (group.negative_prompt || '') : '');
                        message.success(t('ai_prompt_filled'));
                        setAiModalOpen(false);
                        setAiModalIntent('');
                        setAiGeneratedGroups([]);
                      }}
                      style={{ fontSize: 11 }}
                    >
                      {t('ai_prompt_use')}
                    </Button>
                  </div>

                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Prompt:</Text>
                    <div style={{
                      background: '#fff', borderRadius: 4, padding: '6px 8px', marginTop: 2,
                      border: '1px solid #e8e8e8', fontSize: 12, lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflow: 'auto',
                    }}>
                      {group.prompt || '（空）'}
                    </div>
                  </div>

                  {group.negative_prompt && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 11, color: '#faad14' }}>{t('ai_prompt_negative_label')}:</Text>
                      <div style={{
                        background: '#fff', borderRadius: 4, padding: '4px 6px', marginTop: 2,
                        border: '1px solid #fff3e0', fontSize: 11, lineHeight: 1.4,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 60, overflow: 'auto',
                        color: '#8c8c8c',
                      }}>
                        {group.negative_prompt}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            {aiGeneratedGroups.length > 0 && !aiGenerating && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={generateAIPrompt}
                  style={{ fontSize: 12 }}
                >
                  {t('ai_prompt_regenerate')}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ======== 素材库弹窗（3 Tab） ======== */}
      <Modal title={<Space size={8}><PictureOutlined style={{ color: '#1677ff' }} /><span>{t("asset_library_title")}</span></Space>}
        open={imageLibraryOpen} onCancel={() => { setImageLibraryOpen(false); setSelectedMaterialId(null); setSelectedAssetGroupId(''); setAssetList([]); }}
        footer={null} width={820} destroyOnClose
      >
        <Tabs activeKey={libraryMainTab} onChange={key => { setLibraryMainTab(key); setSelectedAssetGroupId(''); setAssetList([]); if (key === 'aigc' || key === 'realperson') fetchAssetGroups(); if (key === 'works') { setMaterialTab(imageLibraryTarget === 'referenceVideos' ? 'video' : 'image'); fetchImageLibrary(1, imageLibraryTarget === 'referenceVideos' ? 'video' : 'image'); } }}
          items={[
            { key: 'works', label: <span><HistoryOutlined style={{ marginRight: 6 }} />{t("my_works")}</span>, children: (
              <div>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{materialTab === 'video' ? t('video_expiry_24h') : t('image_expiry_1h')}</Text>
                  <Space size={4}>
                    <Button size="small" type={materialTab === 'image' ? 'primary' : 'default'} onClick={() => { setMaterialTab('image'); fetchImageLibrary(1, 'image'); }}>Images</Button>
                    <Button size="small" type={materialTab === 'video' ? 'primary' : 'default'} onClick={() => { setMaterialTab('video'); fetchImageLibrary(1, 'video'); }}>Videos</Button>
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchImageLibrary(imageLibraryPage)} />
                  </Space>
                </div>
                {imageLibraryLoading ? (<div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>)
                : imageLibraryItems.length === 0 ? (<Empty description="No materials yet" style={{ padding: '30px' }} />)
                : (<><div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, maxHeight: '46vh', overflow: 'auto' }}>
                  {imageLibraryItems.map(item => { const isV = item._type === 'video'; return (
                    <div key={item.id} onClick={() => selectImageFromLibrary(item)} style={{ width: 180, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.18s', border: selectedMaterialId===item.id ? '2px solid #1677ff' : '1px solid #eee', boxShadow: selectedMaterialId===item.id ? '0 0 0 3px rgba(22,119,255,0.15)' : '0 1px 3px rgba(0,0,0,0.05)', background: '#fff', position: 'relative' }}
                      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)';}} onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow=selectedMaterialId===item.id?'0 0 0 3px rgba(22,119,255,0.15)':'0 1px 3px rgba(0,0,0,0.05)';}}>
                      {selectedMaterialId===item.id && <div style={{position:'absolute',top:6,right:6,zIndex:9,width:22,height:22,borderRadius:'50%',background:'#1677ff',display:'flex',alignItems:'center',justifyContent:'center'}}><CheckCircleOutlined style={{fontSize:13,color:'#fff'}}/></div>}
                      <div style={{width:'100%',height:112,overflow:'hidden',background:'#f0f0f0',position:'relative'}}>
                        {isV ? (item.videoUrl ? <video src={item.videoUrl} muted style={{width:'100%',height:'100%',objectFit:'cover'}}/> : <PlayCircleOutlined style={{fontSize:36,color:'#bbb',margin:'38px 60px'}}/>) : (item.expired ? <PictureOutlined style={{fontSize:36,color:'#ccc',margin:'38px 60px'}}/> : <img src={item.image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>{e.target.style.display='none'}}/>)}
                        {isV && <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.2)'}}><PlayCircleOutlined style={{fontSize:30,color:'rgba(255,255,255,0.85)'}}/></div>}
                        {item.duration>0 && <span style={{position:'absolute',bottom:4,right:4,fontSize:10,color:'#fff',background:'rgba(0,0,0,0.55)',padding:'1px 5px',borderRadius:3}}>{item.duration}s</span>}
                      </div>
                      <div style={{padding:'6px 8px'}}><Text style={{fontSize:11,lineHeight:1.3}} ellipsis={{tooltip:item.prompt}}>{item.prompt||'(no prompt)'}</Text>
                        <div style={{marginTop:3,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <Tag color={item.remaining_seconds>0?'green':'red'} style={{fontSize:9,lineHeight:'14px'}}>{formatRemaining(item.remaining_seconds)}</Tag>
                          <Space size={2}>
                            {!isV && item.image_url && isDownloadableUrl(item.image_url) && (
                              <Tooltip title={t("download_image")}><Button size="small" type="text" icon={<DownloadOutlined />} style={{fontSize:10,padding:'0 2px',minWidth:16,height:18,color:'#1677ff'}}
                                onClick={e=>{e.stopPropagation();downloadSingleFile(item.image_url,'image_'+item.id+'.'+getFileExt(item.image_url));}} /></Tooltip>
                            )}
                            {isV && item.videoUrl && isDownloadableUrl(item.videoUrl) && (
                              <Tooltip title={t("download_video")}><Button size="small" type="text" icon={<DownloadOutlined />} style={{fontSize:10,padding:'0 2px',minWidth:16,height:18,color:'#1677ff'}}
                                onClick={e=>{e.stopPropagation();downloadSingleFile(item.videoUrl,'video_'+item.id+'.mp4');}} /></Tooltip>
                            )}
                            <Button size="small" type="text" icon={<ThunderboltOutlined />} style={{fontSize:10,padding:'0 2px',minWidth:16,height:18,color:'#fa8c16'}}
                              onClick={e=>{e.stopPropagation();openConvertModal({...item,imageUrls:item.image_url?[item.image_url]:[]});}} />
                          </Space>
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
                {imageLibraryTotal>20 && <div style={{textAlign:'center',marginTop:12}}><Space><Button size="small" disabled={imageLibraryPage<=1} onClick={()=>fetchImageLibrary(imageLibraryPage-1)}>Prev</Button><Text type="secondary" style={{fontSize:12}}>{imageLibraryPage}/{Math.ceil(imageLibraryTotal/20)}</Text><Button size="small" disabled={imageLibraryPage*20>=imageLibraryTotal} onClick={()=>fetchImageLibrary(imageLibraryPage+1)}>Next</Button></Space></div>}
                </>)}
              </div>
            )},
            { key: 'aigc', label: <span><ThunderboltOutlined style={{ marginRight: 6 }} />{t("aigc_asset_group")}</span>, children: (
              <div>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ fontSize: 14 }}>{t('asset_group_list')}</Text>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setNewGroupName(''); setNewGroupDesc(''); setGroupModalOpen(true); }}>{t('create_group')}</Button>
                </div>
                {selectedAssetGroupId ? (
                  <div>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space><Button size="small" icon={<LeftOutlined />} onClick={() => { setSelectedAssetGroupId(''); setAssetList([]); }}>{t('back_to_group_list')}</Button><Tag color="blue">{assetGroups.find(g => (g.Id||g.id) === selectedAssetGroupId)?.Name || selectedAssetGroupId}</Tag></Space>
                      <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchAssets(selectedAssetGroupId)}>{t('refresh')}</Button>
                    </div>
                    <div style={{ marginBottom: 14, padding: 12, background: '#fafbfc', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                      <Space size={6} wrap>
                        <Select size="small" value={newAssetType} onChange={setNewAssetType} style={{ width: 90 }} options={[{value:'Image',label:t('image_label')},{value:'Video',label:t('video_label')},{value:'Audio',label:t('audio')}]} />
                        <Input size="small" value={newAssetName} onChange={e=>setNewAssetName(e.target.value)} placeholder={t("enter_asset_name")} style={{ width: 150 }} />
                        <Input size="small" value={newAssetUrl} onChange={e=>setNewAssetUrl(e.target.value)} placeholder={t("paste_asset_url")} style={{ width: 180 }} />
                        <Button size="small" type="primary" onClick={()=>handleCreateAsset()} loading={assetLoading}>{t('submit_url')}</Button>
                        <Upload customRequest={({ file, onSuccess, onError }) => { setAssetLoading(true); multimodalAPI.uploadFile(file).then(res => { if (res.url || res.URL) { setNewAssetUrl(res.url || res.URL); message.success(t('upload_success')); handleCreateAsset(res.url || res.URL); onSuccess(res, file); } else { onError(new Error(res.error || '上传失败')); message.error(res.error || '上传失败'); } }).catch(e => { onError(e); message.error(e.message || ''); }).finally(() => setAssetLoading(false)); }} showUploadList={false} accept="image/*,video/*,audio/*"><Button size="small" icon={<UploadOutlined/>} loading={assetLoading}>{t("upload_asset")}</Button></Upload>
                        
                      </Space>
                    </div>
                    {assetLoading ? <div style={{textAlign:'center',padding:20}}><Spin/></div> :
                     assetList.length===0 ? <Empty description={t("no_assets_in_group")} style={{padding:20}}/> :
                     <div style={{maxHeight:300,overflow:'auto'}}>
                       {assetList.map(a => { const aid=a.Id||a.id; const aurl=a.URL||a.url||''; const aname=a.Name||a.name||aid; const st=a.Status||a.status||'Processing';const atype=a.AssetType||a.asset_type||a.type||'Image';const isVid=atype==='Video'||atype==='video'; return (
                         <div key={aid} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:8,background:'#fff',border:'1px solid #f0f0f0',marginBottom:6}}>
                           <div style={{width:44,height:44,borderRadius:4,overflow:'hidden',flexShrink:0,background:'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center'}}>{aurl ? (isVid ? <VideoCameraOutlined style={{fontSize:20,color:'#722ed1'}}/> : <img src={aurl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>) : <PictureOutlined style={{fontSize:18,color:'#ccc',margin:13}}/>}</div>
                           <div style={{flex:1,minWidth:0}}><Text style={{fontSize:12}} ellipsis>{aname}</Text><div><Tag color={st==='Active'?'green':st==='Failed'?'red':'blue'} style={{fontSize:10,margin:0}}>{st==='Active'?t('asset_active'):st==='Failed'?t('asset_failed'):t('asset_processing')}</Tag>{isVid&&<Tag color="purple" style={{fontSize:10,margin:'0 0 0 4px'}}>{t("video_label")}</Tag>}</div></div>
                           <Space size={4}><Button size="small" type="link" onClick={()=>addAssetAsReference(aid,aurl,aname,atype)} disabled={st!=='Active'} style={{fontSize:11}}>{t('reference')}</Button><Popconfirm title={t("confirm_delete_asset")} cancelText={t("cancel")} okText={t("confirm")} onConfirm={()=>{const isProcessing=(st==="Processing"); if(isProcessing){setAssetList(prev=>prev.filter(i=>(i.Id||i.id)!==aid));message.success(t("delete_success"));return;} multimodalAPI.deleteAsset(aid, userId).then(r=>{if(r.error){message.warning(r.error);fetchAssets(selectedAssetGroupId);}else{message.success(t("delete_success"));fetchAssets(selectedAssetGroupId);}}).catch(()=>{fetchAssets(selectedAssetGroupId);});}}><Button size="small" type="text" danger icon={<DeleteOutlined/>} style={{fontSize:11}}/></Popconfirm></Space>
                         </div>
                       );})}
                     </div>}
                  </div>
                ) : (
                  assetGroups.filter(g => g.GroupType === 'AIGC' || !g.GroupType).length === 0 ? <Empty description={t("no_asset_groups")} style={{ padding: '40px' }} /> :
                  <Table dataSource={assetGroups.filter(g => g.GroupType === 'AIGC' || !g.GroupType)} rowKey={r => r.Id || r.id} size="small" pagination={false}
                    onRow={r => ({ onClick: () => { setSelectedAssetGroupId(r.Id||r.id); fetchAssets(r.Id||r.id); }, style: { cursor: 'pointer' } })}
                    columns={[
                      { title: t('group_name'), dataIndex: 'Name', key: 'name', render: (n,r) => {
                        const name = n || r.name;
                        const desc = r.Description || r.description;
                        const gid = r.Id || r.id;
                        const isEditing = editingGroupId === gid;
                        return (
                          <div onMouseEnter={() => { if (!isEditing) setEditingGroupId(gid+'_hover'); }} onMouseLeave={() => { if (editingGroupId === gid+'_hover') setEditingGroupId(''); }}>
                            {isEditing ? (
                              <Input size="small" value={editingGroupName} onChange={e => setEditingGroupName(e.target.value)}
                                onBlur={() => { const nv = editingGroupName.trim(); if (nv && nv !== name) { multimodalAPI.updateAssetGroup(gid, nv, userId).then(() => fetchAssetGroups()); } setEditingGroupId(''); setEditingGroupName(''); }}
                                onPressEnter={() => { const nv = editingGroupName.trim(); if (nv && nv !== name) { multimodalAPI.updateAssetGroup(gid, nv, userId).then(() => fetchAssetGroups()); } setEditingGroupId(''); setEditingGroupName(''); }}
                                autoFocus style={{ width: '100%' }} onClick={e => e.stopPropagation()} />
                            ) : (
                              <div style={{position:'relative'}}>
                                <Text strong style={{color:'#1677ff',display:'block',lineHeight:1.4}}>{name || '(unnamed)'}</Text>
                                {desc ? <Text type="secondary" style={{fontSize:11,lineHeight:1.3}}>{desc}</Text> : null}
                                {editingGroupId === gid+'_hover' && (
                                  <EditOutlined style={{position:'absolute',top:0,right:0,fontSize:12,color:'#999',cursor:'pointer'}}
                                    onClick={e => { e.stopPropagation(); setEditingGroupId(gid); setEditingGroupName(name || ''); }} />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }},
                      { title: t('group_id'), dataIndex: 'Id', key: 'id', width: 180, render: (id,r) => <Text type="secondary" style={{fontSize:11,fontFamily:'monospace'}}>{id||r.id}</Text> },
                      { title: t('created_at'), dataIndex: 'CreateTime', key: 'time', width: 160, render: t => <Text type="secondary" style={{fontSize:11}}>{t ? new Date(t).toLocaleString() : '-'}</Text> },
                      { title: '', key: 'actions', width: 36, render: (_,r) => (
                        <Popconfirm title={t("confirm_delete")} cancelText={t("cancel")} okText={t("confirm")} onConfirm={e => { e.stopPropagation(); multimodalAPI.deleteAssetGroup(r.Id||r.id, userId).then(() => fetchAssetGroups()); }}>
                          <Button size="small" type="text" icon={<DeleteOutlined style={{color:'#bfbfbf'}}/>} onClick={e => e.stopPropagation()} />
                        </Popconfirm>
                      )},
                    ]}
                  />
                )}
              </div>
            )},
            { key: 'realperson', label: <span><TeamOutlined style={{ marginRight: 6 }} />{t("real_person_group")}</span>, children: (
              <div>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ fontSize: 14 }}>{t('real_person_group_list')}</Text>
                  <Button type="primary" size="small" icon={<ScanOutlined />} onClick={() => { if (!userId) { message.warning('用户信息加载中，请稍后再试...'); return; } setLivenessState('idle'); setLivenessData(null); setLivenessModalOpen(true); startLivenessFlow(); }}>{t('create_real_person_group')}</Button>
                </div>
                {selectedAssetGroupId ? (
                  <div>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space><Button size="small" icon={<LeftOutlined />} onClick={() => { setSelectedAssetGroupId(''); setAssetList([]); }}>{t('back_to_group_list')}</Button><Tag color="blue">{assetGroups.find(g => (g.Id||g.id) === selectedAssetGroupId)?.Name || selectedAssetGroupId}</Tag></Space>
                      <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchAssets(selectedAssetGroupId)}>{t('refresh')}</Button>
                    </div>
                    <div style={{ marginBottom: 14, padding: 12, background: '#fafbfc', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                      <Space size={6} wrap>
                        <Select size="small" value={newAssetType} onChange={setNewAssetType} style={{ width: 90 }} options={[{value:'Image',label:t('image_label')},{value:'Video',label:t('video_label')},{value:'Audio',label:t('audio')}]} />
                        <Input size="small" value={newAssetName} onChange={e=>setNewAssetName(e.target.value)} placeholder={t("enter_asset_name")} style={{ width: 150 }} />
                        <Input size="small" value={newAssetUrl} onChange={e=>setNewAssetUrl(e.target.value)} placeholder={t("paste_asset_url")} style={{ width: 180 }} />
                        <Button size="small" type="primary" onClick={()=>handleCreateAsset()} loading={assetLoading}>{t('submit_url')}</Button>
                        <Upload customRequest={({ file, onSuccess, onError }) => { setAssetLoading(true); multimodalAPI.uploadFile(file).then(res => { if (res.url || res.URL) { setNewAssetUrl(res.url || res.URL); message.success(t('upload_success')); handleCreateAsset(res.url || res.URL); onSuccess(res, file); } else { onError(new Error(res.error || '上传失败')); message.error(res.error || '上传失败'); } }).catch(e => { onError(e); message.error(e.message || ''); }).finally(() => setAssetLoading(false)); }} showUploadList={false} accept="image/*,video/*,audio/*"><Button size="small" icon={<UploadOutlined/>} loading={assetLoading}>{t("upload_asset")}</Button></Upload>
                      </Space>
                    </div>
                    {assetLoading ? <div style={{textAlign:'center',padding:20}}><Spin/></div> :
                     assetList.length===0 ? <Empty description={t("no_assets_in_group")} style={{padding:20}}/> :
                     <div style={{maxHeight:300,overflow:'auto'}}>{assetList.map(a => { const aid=a.Id||a.id; const aurl=a.URL||a.url||''; const aname=a.Name||a.name||aid; const st=a.Status||a.status||'Processing';const atype=a.AssetType||a.asset_type||a.type||'Image';const isVid=atype==='Video'||atype==='video'; return (
                       <div key={aid} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:8,background:'#fff',border:'1px solid #f0f0f0',marginBottom:6}}>
                         <div style={{width:44,height:44,borderRadius:4,overflow:'hidden',flexShrink:0,background:'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center'}}>{aurl ? (isVid ? <VideoCameraOutlined style={{fontSize:20,color:'#722ed1'}}/> : <img src={aurl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>) : <PictureOutlined style={{fontSize:18,color:'#ccc',margin:13}}/>}</div>
                         <div style={{flex:1,minWidth:0}}><Text style={{fontSize:12}} ellipsis>{aname}</Text><div><Tag color={st==='Active'?'green':st==='Failed'?'red':'blue'} style={{fontSize:10,margin:0}}>{st==='Active'?t('asset_active'):st==='Failed'?t('asset_failed'):t('asset_processing')}</Tag>{isVid&&<Tag color="purple" style={{fontSize:10,margin:'0 0 0 4px'}}>{t("video_label")}</Tag>}</div></div>
                         <Space size={4}><Button size="small" type="link" onClick={()=>addAssetAsReference(aid,aurl,aname,atype)} disabled={st!=='Active'} style={{fontSize:11}}>{t('reference')}</Button><Popconfirm title={t("confirm_delete_asset")} cancelText={t("cancel")} okText={t("confirm")} onConfirm={()=>{const isProcessing=(st==="Processing"); if(isProcessing){setAssetList(prev=>prev.filter(i=>(i.Id||i.id)!==aid));message.success(t("delete_success"));return;} multimodalAPI.deleteAsset(aid, userId).then(r=>{if(r.error){message.warning(r.error);fetchAssets(selectedAssetGroupId);}else{message.success(t("delete_success"));fetchAssets(selectedAssetGroupId);}}).catch(()=>{fetchAssets(selectedAssetGroupId);});}}><Button size="small" type="text" danger icon={<DeleteOutlined/>} style={{fontSize:11}}/></Popconfirm></Space>
                       </div>
                     );})}</div>}
                  </div>
                ) : (
                  assetGroups.filter(g => g.GroupType === 'LivenessFace').length === 0 ? <Empty description={t("no_real_person_groups")} style={{ padding: '40px' }} /> :
                  <Table dataSource={assetGroups.filter(g => g.GroupType === 'LivenessFace')} rowKey={r => r.Id || r.id} size="small" pagination={false}
                    onRow={r => ({ onClick: () => { setSelectedAssetGroupId(r.Id||r.id); fetchAssets(r.Id||r.id); }, style: { cursor: 'pointer' } })}
                    columns={[
                      { title: t('group_name'), dataIndex: 'Name', key: 'name', render: (n,r) => {
                        const name = n || r.name;
                        const desc = r.Description || r.description;
                        const gid = r.Id || r.id;
                        const isEditing = editingGroupId === gid;
                        return (
                          <div onMouseEnter={() => { if (!isEditing) setEditingGroupId(gid+'_hover_rp'); }} onMouseLeave={() => { if (editingGroupId === gid+'_hover_rp') setEditingGroupId(''); }}>
                            {isEditing ? (
                              <Input size="small" value={editingGroupName} onChange={e => setEditingGroupName(e.target.value)}
                                onBlur={() => { const nv = editingGroupName.trim(); if (nv && nv !== name) { multimodalAPI.updateAssetGroup(gid, nv, userId).then(() => fetchAssetGroups()); } setEditingGroupId(''); setEditingGroupName(''); }}
                                onPressEnter={() => { const nv = editingGroupName.trim(); if (nv && nv !== name) { multimodalAPI.updateAssetGroup(gid, nv, userId).then(() => fetchAssetGroups()); } setEditingGroupId(''); setEditingGroupName(''); }}
                                autoFocus style={{ width: '100%' }} onClick={e => e.stopPropagation()} />
                            ) : (
                              <div style={{position:'relative'}}>
                                <Text strong style={{color:'#1677ff',display:'block',lineHeight:1.4}}>{name || '(unnamed)'}</Text>
                                {desc ? <Text type="secondary" style={{fontSize:11,lineHeight:1.3}}>{desc}</Text> : null}
                                {editingGroupId === gid+'_hover_rp' && (
                                  <EditOutlined style={{position:'absolute',top:0,right:0,fontSize:12,color:'#999',cursor:'pointer'}}
                                    onClick={e => { e.stopPropagation(); setEditingGroupId(gid); setEditingGroupName(name || ''); }} />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }},
                      { title: t('group_id'), dataIndex: 'Id', key: 'id', width: 180, render: (id,r) => <Text type="secondary" style={{fontSize:11,fontFamily:'monospace'}}>{id||r.id}</Text> },
                      { title: t('created_at'), dataIndex: 'CreateTime', key: 'time', width: 160, render: t => <Text type="secondary" style={{fontSize:11}}>{t ? new Date(t).toLocaleString() : '-'}</Text> },
                      { title: '', key: 'actions', width: 36, render: (_,r) => (
                        <Popconfirm title={t("confirm_delete")} cancelText={t("cancel")} okText={t("confirm")} onConfirm={e => { e.stopPropagation(); multimodalAPI.deleteAssetGroup(r.Id||r.id, userId).then(() => fetchAssetGroups()); }}>
                          <Button size="small" type="text" icon={<DeleteOutlined style={{color:'#bfbfbf'}}/>} onClick={e => e.stopPropagation()} />
                        </Popconfirm>
                      )},
                    ]}
                  />
                )}
              </div>
            )},
          ]}
        />
      </Modal>

      {/* ======== {t('create_group')}弹窗 ======== */}
      <Modal title={t('create_group')} open={groupModalOpen} onCancel={() => setGroupModalOpen(false)} zIndex={1050} onOk={handleCreateAssetGroup} confirmLoading={assetLoading} okText={t('create')} cancelText={t('cancel')}>
        <Form layout="vertical">
          <Form.Item label={t("group_name")} required><Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder={t("enter_group_name")} /></Form.Item>
          <Form.Item label={t("remark")}><Input.TextArea value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} placeholder={t("optional")} rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* ======== 转换到AIGC素材组弹窗 ======== */}
      <Modal title={t("convert_to_aigc_group")} open={convertModalOpen} onCancel={() => setConvertModalOpen(false)} onOk={handleConvertConfirm} okText={t("confirm_convert")} cancelText={t("cancel")} width={400} zIndex={1050}>
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>{t("select_target_group")}</Text>
          <Select value={convertGroupId} onChange={setConvertGroupId} style={{ width: '100%' }}>
            {assetGroups.filter(g => g.GroupType === 'AIGC' || !g.GroupType).map(g => (
              <Select.Option key={g.Id||g.id} value={g.Id||g.id}>{g.Name||g.name||'(unnamed)'}</Select.Option>
            ))}
          </Select>
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>{t('asset_name')}</Text>
          <Input value={convertName} onChange={e => setConvertName(e.target.value)} placeholder={t('enter_asset_name')} maxLength={100} style={{ width: '100%' }} />
        </div>
      </Modal>

      {/* ======== 真人实名弹窗 ======== */}
      <Modal title={t("liveness_verification")} open={livenessModalOpen} onCancel={() => { if(livenessTimerRef.current) clearInterval(livenessTimerRef.current); if(livenessTimeoutRef.current) clearTimeout(livenessTimeoutRef.current); setLivenessModalOpen(false); setLivenessState('idle'); setLivenessData(null); }} footer={null} width={420} destroyOnClose>
        {livenessState === 'idle' || livenessState === 'consent' ? (
          <div style={{textAlign:'center',padding:'20px 0'}}><Spin size="large"/><Text type="secondary" style={{display:'block',marginTop:12}}>Preparing verification...</Text></div>
        ) : livenessState === 'done' ? (
          <div style={{textAlign:'center',padding:'20px 0'}}>
            <CheckCircleOutlined style={{fontSize:48,color:'#52c41a'}}/>
            <Text strong style={{display:'block',marginTop:12,fontSize:15,color:'#52c41a'}}>Verification passed</Text>
            <Text type="secondary" style={{display:'block',marginTop:8}}>Real person group has been created and can be viewed in the list.</Text>
            <Button type="primary" onClick={() => { setLivenessModalOpen(false); setLivenessState('idle'); fetchAssetGroups(); }} style={{marginTop:16}}>OK</Button>
          </div>
        ) : livenessState === 'timeout' ? (
          <div style={{textAlign:'center',padding:'20px 0'}}>
            <ExclamationCircleOutlined style={{fontSize:48,color:'#faad14'}}/>
            <Text strong style={{display:'block',marginTop:12,fontSize:15}}>QR code expired</Text>
            <Text type="secondary" style={{display:'block',marginTop:8}}>The QR code is valid for 150 seconds. Please scan with your phone in time.</Text>
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => startLivenessFlow()} style={{marginTop:16}}>Reload QR Code</Button>
          </div>
        ) : (
          <div style={{textAlign:'center',padding:'10px 0'}}>
            {livenessData?.QRCodeDataURL ? (<><img src={livenessData.QRCodeDataURL} alt="QR" style={{width:200,height:200}}/><Text style={{display:'block',marginTop:12}}>Scan the QR code with your phone to complete liveness verification</Text><Text type="secondary" style={{fontSize:11}}>The QR code expires in 150 seconds</Text></>) : <Spin size="large"/>}
          </div>
        )}
      </Modal>

      {/* ======== 预览弹窗（增强版：多图切换 + 可折叠详情面板） ======== */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => { setPreviewVisible(false); setPreviewImageIndex(0); }}
        width={previewData?.type === 'video' ? 860 : 800}
        title={
          <Space>
            {previewData?.type === 'video' ? <VideoCameraOutlined /> : <PictureOutlined />}
            <Text ellipsis style={{ maxWidth: 420 }}>{previewData?.prompt || t('preview')}</Text>
          </Space>
        }
        centered
      >
        {/* 媒体内容 */}
        {previewData?.type === 'video' ? (
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <video key={previewData.record?.id || previewData.url} controls autoPlay style={{ width: '100%', maxHeight: '60vh', borderRadius: 8, background: '#000', display: 'block' }}>
              <source src={previewData.url} type="video/mp4" />
              {t('browser_not_support_video')}
            </video>
            {isDownloadableUrl(previewData.url) && (
              <Tooltip title={t('download_video')}>
                <Button
                  icon={<DownloadOutlined />} type="primary" ghost size="small"
                  onClick={() => downloadSingleFile(previewData.url, `video_${previewData.record?.id || Date.now()}.mp4`)}
                  style={{
                    position: 'absolute', top: 8, right: 8, zIndex: 10,
                    borderRadius: 6, fontSize: 12,
                  }}
                >
                  {t('download_video')}
                </Button>
              </Tooltip>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {/* 图片容器（支持 hover 显示下载图标） */}
            {previewData?.urls?.length > 0 && (
              <div
                style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
                className="preview-image-wrapper"
              >
                <Image
                  key={previewImageIndex}
                  src={previewData.urls[previewImageIndex]}
                  style={{ maxHeight: '55vh', borderRadius: 8, objectFit: 'contain' }}
                  fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNjY2MiIGZvbnQtc2l6ZT0iMTQiPuWbvueJh+WKoOi9veWksei0pTwvdGV4dD48L3N2Zz4="
                />
                {/* hover 下载图标 - 多图 */}
                {previewData.urls.length > 1 ? (
                  <Popover
                    trigger="click"
                    placement="leftTop"
                    title={null}
                    content={
                      <div style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('download_image')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                          {previewData.urls.map((url, i) => (
                            <div key={i} style={{ position: 'relative', cursor: 'pointer' }}>
                              <img
                                src={url} alt={'img ' + (i + 1)}
                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: previewImageIndex === i ? '2px solid #1677ff' : '1px solid #eee' }}
                                title={t('download_image') + ' ' + (i + 1)}
                              />
                              <DownloadOutlined
                                onClick={() => downloadSingleFile(url, 'image_' + (i + 1) + '.' + getFileExt(url))}
                                style={{
                                  position: 'absolute', bottom: 2, right: 2,
                                  fontSize: 12, color: '#fff', background: 'rgba(0,0,0,0.5)',
                                  borderRadius: 3, padding: 2, cursor: 'pointer',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        {previewData.urls.length > 1 && (
                          <Button block size="small" type="primary" ghost
                            onClick={() => downloadAllAsZip(previewData.urls, 'images_' + (previewData.record?.id || Date.now()))}>
                            {t('download_all_zip')}
                          </Button>
                        )}
                      </div>
                    }
                  >
                    <div
                      className="preview-download-btn"
                      style={{
                        position: 'absolute', bottom: 12, right: 12,
                        width: 32, height: 32, borderRadius: 6,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 5,
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <DownloadOutlined style={{ fontSize: 16, color: '#fff' }} />
                    </div>
                  </Popover>
                ) : (
                  /* hover 下载图标 - 单图（直接下载） */
                  isDownloadableUrl(previewData.urls[0]) && (
                    <div
                      className="preview-download-btn"
                      onClick={() => downloadSingleFile(previewData.urls[0], 'image_' + (previewData.record?.id || Date.now()) + '.' + getFileExt(previewData.urls[0]))}
                      style={{
                        position: 'absolute', bottom: 12, right: 12,
                        width: 32, height: 32, borderRadius: 6,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 5,
                      }}
                    >
                      <DownloadOutlined style={{ fontSize: 16, color: '#fff' }} />
                    </div>
                  )
                )}
                {/* 图片计数 */}
                {previewData.urls.length > 1 && (
                  <Tag color="blue" style={{
                    position: 'absolute', bottom: 12, left: 12,
                    fontSize: 12, borderRadius: 4, opacity: 0.8, margin: 0, zIndex: 5,
                  }}>
                    {previewImageIndex + 1} / {previewData.urls.length}
                  </Tag>
                )}
              </div>
            )}

            {/* 多图切换：左箭头 + 右箭头 */}
            {previewData?.urls?.length > 1 && (
              <>
                <Tooltip title={t('prev_image')}>
                  <Button
                    shape="circle"
                    icon={<span style={{ fontWeight: 'bold', fontSize: 16 }}>‹</span>}
                    style={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, opacity: previewImageIndex === 0 ? 0.3 : 0.85, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    disabled={previewImageIndex === 0}
                    onClick={() => setPreviewImageIndex(i => Math.max(0, i - 1))}
                  />
                </Tooltip>
                <Tooltip title={t('next_image')}>
                  <Button
                    shape="circle"
                    icon={<span style={{ fontWeight: 'bold', fontSize: 16 }}>›</span>}
                    style={{ position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', zIndex: 10, opacity: previewImageIndex >= (previewData.urls.length - 1) ? 0.3 : 0.85, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    disabled={previewImageIndex >= (previewData.urls.length - 1)}
                    onClick={() => setPreviewImageIndex(i => Math.min((previewData.urls?.length || 1) - 1, i + 1))}
                  />
                </Tooltip>
              </>
            )}
          </div>
        )}

        {/* 可折叠详情面板 */}
        {previewData?.record && (
          <Collapse
            ghost
            size="small"
            style={{ marginTop: 16, background: '#fafafa', borderRadius: 8 }}
            items={[{
              key: 'detail',
              label: <Space size={4}><InfoCircleOutlined /><span>{t('detail_info')}</span></Space>,
              children: renderPreviewDetail(previewData.record),
            }]}
          />
        )}
      </Modal>

          </div>
  );
};

export default CreativeInspiration;
